<template>
	<v-card :class="embedded ? '' : 'ma-5'" flat>
		<v-card-text :class="embedded ? 'pa-0' : ''">
			<!-- Current Organization Banner - only show in non-embedded mode -->
			<v-alert
				v-if="!embedded"
				type="info"
				outlined
				dense
				class="mb-4"
				icon="mdi-domain"
			>
				<span class="font-weight-bold">{{ $t('current-organization') }}:</span>
				<span class="font-weight-medium ml-1">{{ organizationName }} ({{ organizationId }})</span>
			</v-alert>

			<!-- Embedded Mode: Direct Action List -->
			<div v-if="embedded" ref="embeddedList" class="embedded-actions-list">
				<div
					v-for="action in availableActions"
					:key="action.key"
					class="action-menu-item"
					:class="{ 'action-disabled': action.key === 'avancieTenants' && !hasAvancieIntegration }"
				>
					<div class="d-flex align-start">
						<v-avatar
							:color="action.color"
							size="36"
							class="mr-3 flex-shrink-0"
						>
							<v-icon color="white" size="18">{{ action.icon }}</v-icon>
						</v-avatar>
						<div class="flex-grow-1 min-width-0">
							<div class="text-subtitle-2 font-weight-bold">
								{{ action.title }}
							</div>
							<div class="text-caption grey--text action-description">
								{{ action.description }}
							</div>
						</div>
						<v-btn
							small
							:color="action.color"
							outlined
							:loading="loading[action.loadingKey]"
							:disabled="anyLoading || (action.key === 'avancieTenants' && !hasAvancieIntegration)"
							class="ml-2 flex-shrink-0"
							@click="executeAction(action)"
						>
							{{ $t('execute') }}
						</v-btn>
					</div>

					<!-- Avancie Tenants Results (shown inline for this action) -->
					<div v-if="action.key === 'avancieTenants' && avancieTenantsResults && avancieTenantsResults.length > 0" class="mt-2 ml-12 avancie-tenants-list">
						<v-list dense class="pa-0">
							<v-list-item
								v-for="(tenant, index) in avancieTenantsResults"
								:key="tenant.id || index"
								class="px-0"
							>
								<v-list-item-content class="py-1">
									<v-list-item-title class="text-body-2">
										{{ tenant.name || tenant.code }}
									</v-list-item-title>
									<v-list-item-subtitle class="d-flex align-center">
										<span class="text-caption">{{ tenant.id }}</span>
										<v-icon
											x-small
											class="ml-1 copy-icon"
											@click.stop="copyToClipboard(tenant.id)"
										>
											mdi-content-copy
										</v-icon>
									</v-list-item-subtitle>
								</v-list-item-content>
							</v-list-item>
						</v-list>
					</div>
					<div v-else-if="action.key === 'avancieTenants' && avancieTenants && avancieTenantsResults.length === 0" class="mt-2 ml-12 text-caption grey--text">
						{{ $t('no-avancie-tenants') }}
					</div>
				</div>
			</div>

			<!-- Non-embedded Mode: Action Selector Menu -->
			<v-menu
				v-else
				v-model="actionsMenuOpen"
				:close-on-content-click="false"
				offset-y
				max-height="500"
				min-width="600"
				max-width="700"
			>
				<template #activator="{ on, attrs }">
					<v-card
						outlined
						v-bind="attrs"
						class="action-menu-btn d-flex align-center pa-4"
						:class="{ 'action-menu-btn--active': actionsMenuOpen }"
						v-on="on"
					>
						<v-avatar color="primary" size="40" class="mr-4">
							<v-icon color="white">mdi-cog-play</v-icon>
						</v-avatar>
						<div class="flex-grow-1">
							<div class="text-subtitle-1 font-weight-medium primary--text">
								{{ $t('select-action') }}
							</div>
							<div class="text-caption grey--text">
								{{ $t('select-action-hint') }}
							</div>
						</div>
						<v-icon color="primary" size="24">
							{{ actionsMenuOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
						</v-icon>
					</v-card>
				</template>

				<v-card ref="menuCard" class="actions-menu-card">
					<v-card-text class="pa-2">
						<div
							v-for="action in availableActions"
							:key="action.key"
							class="action-menu-item"
							:class="{ 'action-disabled': action.key === 'avancieTenants' && !hasAvancieIntegration }"
						>
							<div class="d-flex align-start">
								<v-avatar
									:color="action.color"
									size="36"
									class="mr-3 flex-shrink-0"
								>
									<v-icon color="white" size="18">{{ action.icon }}</v-icon>
								</v-avatar>
								<div class="flex-grow-1 min-width-0">
									<div class="text-subtitle-2 font-weight-bold">
										{{ action.title }}
									</div>
									<div class="text-caption grey--text action-description">
										{{ action.description }}
									</div>
								</div>
								<v-btn
									small
									:color="action.color"
									outlined
									:loading="loading[action.loadingKey]"
									:disabled="anyLoading || (action.key === 'avancieTenants' && !hasAvancieIntegration)"
									class="ml-2 flex-shrink-0"
									@click="executeAction(action)"
								>
									{{ $t('execute') }}
								</v-btn>
							</div>

							<!-- Avancie Tenants Results (shown inline for this action) -->
							<div v-if="action.key === 'avancieTenants' && avancieTenantsResults && avancieTenantsResults.length > 0" class="mt-2 ml-12 avancie-tenants-list">
								<v-list dense class="pa-0">
									<v-list-item
										v-for="(tenant, index) in avancieTenantsResults"
										:key="tenant.id || index"
										class="px-0"
									>
										<v-list-item-content class="py-1">
											<v-list-item-title class="text-body-2">
												{{ tenant.name || tenant.code }}
											</v-list-item-title>
											<v-list-item-subtitle class="d-flex align-center">
												<span class="text-caption">{{ tenant.id }}</span>
												<v-icon
													x-small
													class="ml-1 copy-icon"
													@click.stop="copyToClipboard(tenant.id)"
												>
													mdi-content-copy
												</v-icon>
											</v-list-item-subtitle>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</div>
							<div v-else-if="action.key === 'avancieTenants' && avancieTenants && avancieTenantsResults.length === 0" class="mt-2 ml-12 text-caption grey--text">
								{{ $t('no-avancie-tenants') }}
							</div>
						</div>
					</v-card-text>
				</v-card>
			</v-menu>
		</v-card-text>

		<confirmation-dialog ref="dialogRef" content-class="devops-subdialog" />

		<!-- Orphan Report Dialog -->
		<v-dialog v-model="orphanReportDialog" max-width="860" persistent content-class="devops-subdialog devops-subdialog--scrollable">
			<v-card class="py-6 px-3 orphan-report-card">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('orphan-report-title') }}
					<v-spacer />
					<v-btn icon small @click="closeOrphanReportDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<!-- Category selector: scan one category at a time to keep each request fast -->
					<div class="mb-4">
						<div class="text-caption font-weight-bold mb-1">
							{{ $t('orphan-report-select-category') }}
						</div>
						<div class="d-flex flex-wrap">
							<v-btn
								v-for="cat in orphanCategories"
								:key="cat.category"
								small
								outlined
								class="mr-2 mb-2"
								:color="orphanCategoryResults[cat.category] ? 'success' : 'primary'"
								:loading="loadingCategory === cat.category"
								:disabled="loading.orphanReport && loadingCategory !== cat.category"
								@click="fetchOrphanReport(cat.category)"
							>
								<v-icon v-if="orphanCategoryResults[cat.category]" left small>
									mdi-check
								</v-icon>
								{{ $t('orphan-category-' + cat.category) }}
								<span class="ml-1 grey--text text--darken-1">({{ cat.collections.length }})</span>
							</v-btn>
						</div>
					</div>

					<v-divider class="mb-4" />

					<div v-if="loading.orphanReport && !orphanReport" class="d-flex justify-center py-8">
						<v-progress-circular indeterminate color="primary" />
					</div>

					<div v-else-if="orphanReport" class="orphan-report-panel">
						<div class="d-flex align-center justify-space-between flex-wrap mb-2">
							<div class="text-subtitle-2 font-weight-bold">
								{{ $t('orphan-report-summary-title') }}
							</div>
							<v-chip
								small
								color="error"
								text-color="white"
								class="mt-1"
							>
								{{ orphanReport.summary?.cleanupEligibleFindings || 0 }} {{ $t('orphan-report-cleanup-candidates') }}
							</v-chip>
						</div>

						<div class="d-flex flex-wrap orphan-report-metrics mb-2">
							<div class="orphan-report-metric">
								<div class="text-caption grey--text">
									{{ $t('orphan-report-scanned-documents') }}
								</div>
								<div class="font-weight-bold">
									{{ orphanReport.scannedDocuments || 0 }}
								</div>
							</div>
							<div class="orphan-report-metric">
								<div class="text-caption grey--text">
									{{ $t('orphan-report-total-findings') }}
								</div>
								<div class="font-weight-bold">
									{{ orphanReport.summary?.totalOrphanFindings || 0 }}
								</div>
							</div>
						</div>

						<div v-if="orphanByMissingReference.length" class="mb-2">
							<div class="text-caption font-weight-bold mb-1">
								{{ $t('orphan-report-by-collection') }}
							</div>

							<div v-for="group in orphanByMissingReference" :key="group.type" class="mb-3">
								<div class="text-caption font-weight-bold mb-1 d-flex align-center">
									<span v-if="group.type === 'organization'" class="mr-1">❌</span>
									<span v-else-if="group.type === 'user-group'" class="mr-1">👥</span>
									<span v-else-if="group.type === 'voicemail'" class="mr-1">📱</span>
									<span v-else class="mr-1">⚠️</span>
									{{ $t(`orphan-missing-${group.type}`) }} ({{ group.total }})
								</div>
								<div class="ml-2">
									<v-chip
										v-for="entry in group.columns"
										:key="entry.collection"
										small
										clickable
										class="mr-1 mb-1"
										:color="group.type === 'organization' ? 'error' : 'warning'"
										text-color="white"
										@click="selectOrphanCollection(entry.collection, group.type)"
									>
										{{ entry.collection }}: {{ entry.count }}
									</v-chip>
								</div>
							</div>
						</div>

						<!-- Selected Collection Detail View (at top) -->
						<div v-if="selectedCollectionFindings.length" ref="selectedCollectionPanel" class="mb-3 pa-3 blue-grey lighten-5 rounded">
							<div class="d-flex justify-space-between align-center mb-3">
								<div class="text-subtitle-2 font-weight-bold">
									📋 {{ selectedOrphanCollection }} ({{ selectedCollectionFindings.length }} {{ $t('documents') }})
								</div>
								<v-btn small text @click="selectedOrphanCollection = null; selectedOrphanMissingType = null">
									{{ $t('close') }}
								</v-btn>
							</div>
							<div class="orphan-collection-details" style="max-height: 400px; overflow-y: auto;">
								<div v-for="group in selectedCollectionFindingsGrouped" :key="group.organizationId" class="mb-3">
									<div class="text-caption font-weight-bold pa-2 rounded" style="background-color: #eceff1;">
										{{ $t('orphan-ghost-org-id') }}: <span class="font-monospace">{{ group.organizationId }}</span>
									</div>
									<div class="pl-3 mt-1">
										<div v-for="doc in group.documents" :key="doc.documentId" class="text-caption mb-1">
											{{ doc.documentId }}
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Ghost Organization IDs Section (Collapsible) -->
						<v-expansion-panels v-if="uniqueGhostOrganizations.length" v-model="orphanGhostOrgExpanded" class="mb-3">
							<v-expansion-panel>
								<template #header>
									<div class="text-caption font-weight-bold">
										👻 {{ $t('orphan-ghost-org-ids') }} ({{ uniqueGhostOrganizations.length }})
									</div>
								</template>
								<v-expansion-panel-content>
									<div class="orphan-ghost-ids">
										<div
											v-for="orgId in uniqueGhostOrganizations"
											:key="orgId"
											class="text-caption font-monospace mb-1"
										>
											{{ orgId }}
										</div>
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>

						<!-- Findings Preview Section (Collapsible) -->
						<v-expansion-panels v-if="orphanFindingsPreview.length" v-model="orphanFindingsExpanded" class="mb-2">
							<v-expansion-panel>
								<template #header>
									<div class="text-caption font-weight-bold">
										{{ $t('orphan-report-findings-preview') }}
									</div>
								</template>
								<v-expansion-panel-content>
									<div
										v-for="(finding, index) in orphanFindingsPreview"
										:key="`${finding.collection}-${finding.documentId}-${index}`"
										class="text-caption orphan-finding-line"
									>
										{{ finding.collection }}/{{ finding.documentId }} - {{ finding.missingReference }}
									</div>
									<div v-if="orphanReport.findings?.length > orphanPreviewLimit" class="text-caption grey--text mt-1">
										{{ $t('orphan-report-preview-truncated', { count: orphanReport.findings.length - orphanPreviewLimit }) }}
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</div>

					<div v-else class="text-caption grey--text text-center py-6">
						{{ $t('orphan-report-select-category-hint') }}
					</div>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						@click="closeOrphanReportDialog"
					>
						{{ $t('close') }}
					</v-btn>

					<div class="d-flex align-center flex-wrap justify-end">
						<v-btn
							text
							color="primary"
							class="mr-2"
							:loading="loading.orphanReport"
							:disabled="!hasOrphanResults"
							@click="refreshOrphanReport"
						>
							{{ $t('refresh') }}
						</v-btn>
					</div>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Migrate Conversations Dialog -->
		<v-dialog v-model="migrateDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('migrate-conversations') }}
					<v-spacer />
					<v-btn icon small @click="closeMigrateDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-form ref="migrateForm" v-model="migrateFormValid">
						<v-autocomplete
							v-model="migrateData.from"
							:items="fromPhoneNumberOptions"
							:label="$t('from-phone-number')"
							:rules="[rules.required]"
							:loading="loadingFromPhones"
							outlined
							dense
							item-text="label"
							item-value="phoneNumber"
							class="mb-2"
						>
							<template #item="{ item }">
								<v-list-item-content>
									<v-list-item-title :class="{ 'warning--text': item.isUnassigned }">
										{{ item.label }}
									</v-list-item-title>
									<v-list-item-subtitle>{{ item.phoneNumber }}</v-list-item-subtitle>
								</v-list-item-content>
							</template>
							<template #selection="{ item }">
								<span :class="{ 'warning--text': item.isUnassigned }">
									{{ item.label }} - {{ item.phoneNumber }}
								</span>
							</template>
						</v-autocomplete>

						<v-select
							v-model="migrateData.to"
							:items="toPhoneNumberOptions"
							:label="$t('to-phone-number')"
							:rules="[rules.required]"
							:loading="loadingGroups"
							outlined
							dense
							item-text="label"
							item-value="phoneNumber"
							hide-details
						>
							<template #item="{ item }">
								<v-list-item-content>
									<v-list-item-title>{{ item.label }}</v-list-item-title>
									<v-list-item-subtitle>{{ item.phoneNumber }}</v-list-item-subtitle>
								</v-list-item-content>
							</template>
							<template #selection="{ item }">
								<span>{{ item.label }} - {{ item.phoneNumber }}</span>
							</template>
						</v-select>
					</v-form>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						@click="closeMigrateDialog"
					>
						{{ $t('cancel') }}
					</v-btn>

					<v-btn
						color="primary"
						:disabled="!migrateFormValid"
						:loading="loading.migrate"
						class="px-4"
						@click="confirmMigrate"
					>
						{{ $t('confirm') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Portability Check Dialog -->
		<v-dialog v-model="portabilityDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('portability-check') }}
					<v-spacer />
					<v-btn icon small @click="closePortabilityDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-form ref="portabilityForm" v-model="portabilityFormValid" @submit.prevent>
						<v-text-field
							v-model="portabilityPhone"
							:label="$t('source-phone-number')"
							:rules="[rules.required]"
							:disabled="portabilityResult !== null"
							outlined
							dense
							placeholder="+15551234567"
							hide-details="auto"
							@keyup.enter="portabilityFormValid && !portabilityResult && checkPortability()"
						/>
					</v-form>

					<!-- Portability Results -->
					<div v-if="portabilityResult" class="mt-4">
						<!-- Complete Porting Section -->
						<div class="portability-section mb-4">
							<div class="d-flex align-center mb-2">
								<span class="text-subtitle-1 font-weight-bold">Complete Porting</span>
								<span class="ml-2 text-h6">
									{{ getPortingStatusEmoji(portabilityResult.portingPortability?.portable) }}
								</span>
							</div>
							<div v-if="portabilityResult.portingPortability?.notPortableReason" class="ml-4 text-body-2">
								<div class="font-weight-bold black--text">
									notPortableReason:
								</div>
								<a
									:href="PORTABILITY_API_DOCS_URL"
									target="_blank"
									rel="noopener noreferrer"
								>
									{{ portabilityResult.portingPortability.notPortableReason }}
								</a>
							</div>
						</div>

						<v-divider class="my-3" />

						<!-- Hosted SMS Porting Section -->
						<div class="portability-section">
							<div class="d-flex align-center mb-2">
								<span class="text-subtitle-1 font-weight-bold">Hosted SMS Porting</span>
								<span class="ml-2 text-h6">
									{{ getHostedSmsStatusEmoji(portabilityResult.hostedSmsPortability?.eligibility_status) }}
								</span>
							</div>
							<div class="ml-4 text-body-2">
								<div v-if="portabilityResult.hostedSmsPortability?.ineligibility_reason" class="mb-4">
									<div class="font-weight-bold black--text">
										ineligibility_reason:
									</div>
									<a
										:href="HOSTED_NUMBERS_DOCS_URL"
										target="_blank"
										rel="noopener noreferrer"
									>
										{{ portabilityResult.hostedSmsPortability.ineligibility_reason }}
									</a>
								</div>
								<div v-if="portabilityResult.hostedSmsPortability?.eligibility_sub_status" class="mb-4">
									<div class="font-weight-bold black--text">
										eligibility_sub_status:
									</div>
									<a
										:href="HOSTED_NUMBERS_DOCS_URL"
										target="_blank"
										rel="noopener noreferrer"
									>
										{{ portabilityResult.hostedSmsPortability.eligibility_sub_status }}
									</a>
								</div>
								<div v-if="portabilityResult.hostedSmsPortability?.next_step" class="mb-4">
									<div class="font-weight-bold black--text">
										next_step:
									</div>
									<a
										:href="HOSTED_NUMBERS_DOCS_URL"
										target="_blank"
										rel="noopener noreferrer"
									>
										{{ portabilityResult.hostedSmsPortability.next_step }}
									</a>
								</div>
								<div v-if="portabilityResult.hostedSmsPortability?.phone_number_type">
									<div class="font-weight-bold black--text">
										phone_number_type:
									</div>
									<a
										:href="HOSTED_NUMBERS_DOCS_URL"
										target="_blank"
										rel="noopener noreferrer"
									>
										{{ portabilityResult.hostedSmsPortability.phone_number_type }}
									</a>
								</div>
							</div>
						</div>
					</div>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						@click="closePortabilityDialog"
					>
						{{ portabilityResult ? $t('close') : $t('cancel') }}
					</v-btn>

					<v-btn
						v-if="!portabilityResult"
						color="primary"
						:disabled="!portabilityFormValid"
						:loading="loading.portabilityCheck"
						class="px-4"
						@click="checkPortability"
					>
						{{ $t('execute') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Phone Webhooks Audit Dialog -->
		<v-dialog v-model="phoneWebhooksDialog" max-width="700" persistent content-class="devops-subdialog devops-subdialog--scrollable">
			<v-card class="py-6 px-3 phone-webhooks-card">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('phone-webhooks') }}
					<v-spacer />
					<v-btn icon small @click="closePhoneWebhooksDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-switch
						v-model="phoneWebhooksAllOrgs"
						:label="$t('phone-webhooks-all-orgs')"
						:disabled="loading.phoneWebhooks || repairingWebhooks"
						dense
						hide-details
						class="mt-0 mb-4"
					/>

					<div v-if="loading.phoneWebhooks && !phoneWebhooksResult" class="d-flex justify-center py-8">
						<v-progress-circular indeterminate color="primary" />
					</div>

					<div v-else-if="phoneWebhooksResult">
						<div class="text-caption font-weight-bold mb-1">
							{{ $t('phone-webhooks-expected') }}
						</div>
						<div class="text-caption font-monospace mb-1">
							statusCallback: {{ phoneWebhooksResult.expected?.statusCallback }}
						</div>
						<div class="text-caption font-monospace mb-3">
							voiceFallbackUrl: {{ phoneWebhooksResult.expected?.voiceFallbackUrl }}
						</div>

						<v-divider class="mb-3" />

						<v-alert v-if="!phoneWebhooksResult.mismatches?.length" type="success" dense outlined>
							{{ $t('phone-webhooks-no-mismatches') }}
						</v-alert>

						<div v-else>
							<div class="text-subtitle-2 font-weight-bold mb-2">
								{{ phoneWebhooksResult.mismatches.length }} {{ $t('phone-webhooks-mismatches') }}
							</div>
							<div
								v-for="mismatch in phoneWebhooksResult.mismatches"
								:key="mismatch.sid"
								class="mb-3 pa-2 rounded blue-grey lighten-5"
							>
								<div class="text-body-2 font-weight-bold">
									{{ mismatch.number }} <span class="text-caption grey--text">({{ mismatch.organization }})</span>
								</div>
								<div class="text-caption font-monospace">
									statusCallback: {{ mismatch.statusCallback || '—' }}
								</div>
								<div class="text-caption font-monospace">
									voiceFallbackUrl: {{ mismatch.voiceFallbackUrl || '—' }}
								</div>
							</div>
						</div>

						<v-alert
							v-if="phoneWebhooksResult.repaired"
							type="success"
							dense
							outlined
							class="mt-2"
						>
							{{ $t('phone-webhooks-repaired', { count: phoneWebhooksResult.repaired }) }}
						</v-alert>

						<v-alert
							v-for="(webhookError, index) in phoneWebhooksResult.errors || []"
							:key="index"
							type="error"
							dense
							outlined
							class="mt-2"
						>
							{{ webhookError }}
						</v-alert>
					</div>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn color="error" class="px-4" @click="closePhoneWebhooksDialog">
						{{ $t('close') }}
					</v-btn>

					<div>
						<v-btn
							text
							color="primary"
							class="mr-2"
							:loading="loading.phoneWebhooks"
							:disabled="repairingWebhooks"
							@click="fetchPhoneWebhooks()"
						>
							{{ phoneWebhooksResult ? $t('refresh') : $t('execute') }}
						</v-btn>
						<v-btn
							v-if="phoneWebhooksResult && phoneWebhooksResult.mismatches?.length"
							color="warning"
							:loading="repairingWebhooks"
							:disabled="loading.phoneWebhooks"
							@click="repairPhoneWebhooks"
						>
							{{ $t('phone-webhooks-repair') }}
						</v-btn>
					</div>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- OAuth2 Debug Dialog -->
		<v-dialog v-model="oauth2DebugDialog" max-width="800" persistent content-class="devops-subdialog devops-subdialog--scrollable">
			<v-card class="py-6 px-3 oauth2-debug-card">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('oauth2-debug') }}
					<v-spacer />
					<v-btn icon small @click="closeOAuth2DebugDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<!-- Loading State -->
					<div v-if="loading.oauth2Debug" class="d-flex justify-center py-4">
						<v-progress-circular indeterminate color="teal" />
					</div>

					<!-- No Results -->
					<div v-else-if="oauth2DebugResult && oauth2DebugResult.length === 0" class="text-center py-4">
						<v-icon size="48" color="grey lighten-1">mdi-email-off</v-icon>
						<div class="text-body-1 grey--text mt-2">
							{{ $t('oauth2-debug-no-groups') }}
						</div>
					</div>

					<!-- Results -->
					<div v-else-if="oauth2DebugResult">
						<v-expansion-panels accordion>
							<v-expansion-panel
								v-for="group in oauth2DebugResult"
								:key="group.id"
							>
								<v-expansion-panel-header>
									<div class="d-flex align-center">
										<!-- Status Icon -->
										<template v-if="group.oauth">
											<v-tooltip bottom>
												<template #activator="{ on, attrs }">
													<v-icon color="error" class="mr-3" v-bind="attrs" v-on="on">
														mdi-alert-circle
													</v-icon>
												</template>
												<span>{{ group.oauth }}</span>
											</v-tooltip>
										</template>
										<template v-else-if="group.emailValid === true && hasOAuthWarning(group)">
											<v-icon color="warning" class="mr-3">mdi-alert</v-icon>
										</template>
										<template v-else-if="group.emailValid === true">
											<v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
										</template>
										<template v-else-if="group.emailValid === false">
											<v-icon color="error" class="mr-3">mdi-close-circle</v-icon>
										</template>
										<template v-else>
											<v-icon color="grey" class="mr-3">mdi-help-circle</v-icon>
										</template>

										<!-- Group Info -->
										<div class="d-flex flex-column">
											<span class="font-weight-medium">{{ group.label || '—' }}</span>
											<span class="text-caption grey--text">{{ group.email }}</span>
											<div
												v-for="cred in (group.credentialIds || [])"
												:key="'id-' + cred.id"
												class="d-flex align-center"
											>
												<span class="text-caption grey--text text--lighten-1" style="font-size: 10px !important;">{{ cred.id }}</span>
												<v-btn
													icon
													x-small
													class="ml-1"
													@click.stop="copyToClipboard(cred.id)"
												>
													<v-icon style="font-size: 12px;" color="grey">mdi-content-copy</v-icon>
												</v-btn>
											</div>
										</div>

										<v-spacer />

										<!-- Type Chip -->
										<v-chip
											v-if="group.emailType"
											small
											:color="group.emailType === 'google' ? 'red' : 'blue'"
											text-color="white"
											class="mr-4"
										>
											{{ group.emailType === 'google' ? 'Google' : 'Microsoft' }}
										</v-chip>

										<!-- Delete Credentials -->
										<v-tooltip
											v-for="cred in (group.credentialIds || [])"
											:key="cred.id"
											bottom
										>
											<template #activator="{ on, attrs }">
												<v-btn
													icon
													small
													color="error"
													class="mr-1"
													:loading="deleteCredentialLoading[cred.id]"
													v-bind="attrs"
													v-on="on"
													@click.stop="deleteOAuthCredential(group, cred)"
												>
													<v-icon small>mdi-delete</v-icon>
												</v-btn>
											</template>
											<span>{{ $t('oauth2-debug-delete-credential', { type: cred.type }) }}</span>
										</v-tooltip>
									</div>
								</v-expansion-panel-header>

								<v-expansion-panel-content>
									<!-- OAuth Error Alert -->
									<v-alert
										v-if="group.oauth"
										type="error"
										prominent
										dense
										class="mb-4"
									>
										<div class="font-weight-bold">
											{{ $t('oauth2-debug-oauth-error') }}
										</div>
										<code class="oauth-error-code error--text text--darken-2 font-weight-bold">
											{{ group.oauth }}
										</code>
									</v-alert>

									<!-- Google User Info (displayed first, before Watch Status) -->
									<div v-if="group.emailType === 'google' && group.userInfo" class="mb-4">
										<div class="text-subtitle-2 font-weight-bold mb-2">
											{{ $t('oauth2-debug-google-user-info') }}
										</div>

										<v-card outlined class="pa-3">
											<div class="d-flex align-center oauth-user-card">
												<!-- User Avatar -->
												<v-avatar size="56" class="mr-4">
													<v-img
														v-if="group.userInfo.picture"
														:src="group.userInfo.picture"
														:alt="group.userInfo.name"
													/>
													<v-icon v-else size="56" color="grey lighten-1">mdi-account-circle</v-icon>
												</v-avatar>

												<!-- User Details -->
												<div class="flex-grow-1">
													<div class="d-flex align-center mb-1">
														<span class="text-body-1 font-weight-medium">{{ group.userInfo.name }}</span>
														<v-icon
															v-if="group.userInfo.verified_email"
															small
															color="success"
															class="ml-2"
														>
															mdi-check-decagram
														</v-icon>
													</div>
													<div class="text-body-2 grey--text">
														{{ group.userInfo.email }}
													</div>
													<div v-if="group.userInfo.hd" class="text-caption grey--text">
														{{ $t('oauth2-debug-domain') }}: {{ group.userInfo.hd }}
													</div>
												</div>

												<!-- Additional Info -->
												<div class="oauth-id-info">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-google-id') }}
													</div>
													<code class="text-caption">
														{{ group.userInfo.id }}
													</code>
												</div>
											</div>
										</v-card>
									</div>

									<!-- Google User Info Error -->
									<div v-else-if="group.emailType === 'google' && !group.userInfo" class="mb-4">
										<div class="text-subtitle-2 font-weight-bold mb-2">
											{{ $t('oauth2-debug-google-user-info') }}
										</div>
										<v-alert
											type="warning"
											dense
											outlined
											icon="mdi-account-alert"
											class="mb-0"
										>
											<div class="d-flex align-center">
												<span>{{ $t('oauth2-debug-no-user-info') }}</span>
											</div>
										</v-alert>
									</div>

									<!-- Google Token Scopes -->
									<div v-if="group.emailType === 'google' && group.tokenScopes && group.tokenScopes.length > 0" class="mb-4">
										<div class="text-subtitle-2 font-weight-bold mb-2">
											{{ $t('oauth2-debug-token-scopes') }}
										</div>

										<v-card outlined class="pa-3">
											<div class="d-flex flex-wrap">
												<v-chip
													v-for="scope in group.tokenScopes"
													:key="scope"
													small
													outlined
													color="red"
													class="mr-2 mb-2"
												>
													{{ formatScope(scope) }}
												</v-chip>
											</div>
										</v-card>
									</div>

									<!-- Google Token Scopes Error -->
									<div v-else-if="group.emailType === 'google' && (!group.tokenScopes || group.tokenScopes.length === 0)" class="mb-4">
										<div class="text-subtitle-2 font-weight-bold mb-2">
											{{ $t('oauth2-debug-token-scopes') }}
										</div>
										<v-alert
											type="warning"
											dense
											outlined
											icon="mdi-key-alert"
											class="mb-0"
										>
											<div class="d-flex align-center">
												<span>{{ $t('oauth2-debug-no-token-scopes') }}</span>
											</div>
										</v-alert>
									</div>

									<!-- Google Watch Status -->
									<div v-if="group.emailType === 'google' && group.watchStatus" class="mb-4">
										<div class="d-flex align-center justify-space-between mb-2">
											<div class="text-subtitle-2 font-weight-bold">
												{{ $t('oauth2-debug-gmail-watch-status') }}
											</div>
											<v-btn
												small
												color="red"
												outlined
												:loading="resubscribeLoading[group.id]"
												:disabled="anyResubscribeLoading"
												@click="forceResubscribe(group.id)"
											>
												<v-icon small left>mdi-refresh</v-icon>
												{{ $t('oauth2-debug-resubscribe') }}
											</v-btn>
										</div>

										<v-card
											outlined
											class="pa-3"
											:class="getWatchStatusCardClass(group.watchStatus)"
										>
											<div class="d-flex flex-wrap">
												<!-- Watch Active Status -->
												<div class="watch-status-field mr-6 mb-2">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-watch-active') }}
													</div>
													<div class="d-flex align-center">
														<v-icon
															small
															:color="group.watchStatus.watchActive ? 'success' : 'error'"
															class="mr-1"
														>
															{{ group.watchStatus.watchActive ? 'mdi-check-circle' : 'mdi-close-circle' }}
														</v-icon>
														<span
															class="text-body-2 font-weight-medium"
															:class="group.watchStatus.watchActive ? 'success--text' : 'error--text'"
														>
															{{ group.watchStatus.watchActive ? $t('yes') : $t('no') }}
														</span>
													</div>
												</div>

												<!-- Response Time -->
												<div v-if="group.watchStatus.responseTimeMs" class="watch-status-field mr-6 mb-2">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-response-time') }}
													</div>
													<div class="d-flex align-center">
														<v-icon small color="info" class="mr-1">mdi-timer-outline</v-icon>
														<span class="text-body-2">
															{{ formatResponseTime(group.watchStatus.responseTimeMs) }}
														</span>
													</div>
												</div>

												<!-- Test Success -->
												<div class="watch-status-field mr-6 mb-2">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-test-success') }}
													</div>
													<div class="d-flex align-center">
														<v-icon
															small
															:color="group.watchStatus.success ? 'success' : 'error'"
															class="mr-1"
														>
															{{ group.watchStatus.success ? 'mdi-check' : 'mdi-close' }}
														</v-icon>
														<span class="text-body-2">
															{{ group.watchStatus.success ? $t('yes') : $t('no') }}
														</span>
													</div>
												</div>

												<!-- Message ID -->
												<div v-if="group.watchStatus.messageId" class="watch-status-field-full mb-2">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-message-id') }}
													</div>
													<code class="text-body-2">
														{{ group.watchStatus.messageId }}
													</code>
												</div>

												<!-- Error Message -->
												<div v-if="group.watchStatus.error" class="watch-status-field-full">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-error') }}
													</div>
													<div class="text-body-2 error--text">
														{{ group.watchStatus.error }}
													</div>
												</div>
											</div>
										</v-card>
									</div>

									<!-- Google Watch Status Error -->
									<div v-else-if="group.emailType === 'google' && !group.watchStatus" class="mb-4">
										<div class="d-flex align-center justify-space-between mb-2">
											<div class="text-subtitle-2 font-weight-bold">
												{{ $t('oauth2-debug-gmail-watch-status') }}
											</div>
											<v-btn
												small
												color="red"
												outlined
												:loading="resubscribeLoading[group.id]"
												:disabled="anyResubscribeLoading"
												@click="forceResubscribe(group.id)"
											>
												<v-icon small left>mdi-refresh</v-icon>
												{{ $t('oauth2-debug-resubscribe') }}
											</v-btn>
										</div>
										<v-alert
											type="warning"
											dense
											outlined
											icon="mdi-eye-off"
											class="mb-0"
										>
											<div class="d-flex align-center">
												<span>{{ $t('oauth2-debug-no-watch-status') }}</span>
											</div>
										</v-alert>
									</div>

									<!-- Microsoft User Info -->
									<div v-if="group.emailType === 'MICROSOFT' && group.userInfo" class="mb-4">
										<div class="text-subtitle-2 font-weight-bold mb-2">
											{{ $t('oauth2-debug-microsoft-user-info') }}
										</div>

										<v-card outlined class="pa-3">
											<div class="d-flex align-center oauth-user-card">
												<!-- User Avatar -->
												<v-avatar size="56" class="mr-4" color="blue lighten-4">
													<v-icon size="32" color="blue">mdi-microsoft</v-icon>
												</v-avatar>

												<!-- User Details -->
												<div class="flex-grow-1">
													<div class="d-flex align-center mb-1">
														<span class="text-body-1 font-weight-medium">
															{{ group.userInfo.displayName }}
														</span>
													</div>
													<div class="text-body-2 grey--text">
														{{ group.userInfo.mail || group.userInfo.userPrincipalName }}
													</div>
													<div v-if="group.userInfo.jobTitle" class="text-caption grey--text">
														{{ group.userInfo.jobTitle }}
														<span v-if="group.userInfo.officeLocation">
															• {{ group.userInfo.officeLocation }}
														</span>
													</div>
													<div v-if="group.userInfo.preferredLanguage" class="text-caption grey--text">
														{{ $t('oauth2-debug-language') }}: {{ group.userInfo.preferredLanguage }}
													</div>
												</div>

												<!-- Additional Info -->
												<div class="oauth-id-info">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-microsoft-id') }}
													</div>
													<code class="text-caption">
														{{ group.userInfo.id }}
													</code>
													<div v-if="group.userInfo.mobilePhone" class="mt-1">
														<div class="text-caption grey--text">
															{{ $t('oauth2-debug-mobile') }}
														</div>
														<span class="text-caption">
															{{ group.userInfo.mobilePhone }}
														</span>
													</div>
												</div>
											</div>
										</v-card>
									</div>

									<!-- Microsoft User Info Error -->
									<div v-else-if="group.emailType === 'MICROSOFT' && !group.userInfo" class="mb-4">
										<div class="text-subtitle-2 font-weight-bold mb-2">
											{{ $t('oauth2-debug-microsoft-user-info') }}
										</div>
										<v-alert
											type="warning"
											dense
											outlined
											icon="mdi-account-alert"
											class="mb-0"
										>
											<div class="d-flex align-center">
												<span>{{ $t('oauth2-debug-no-user-info') }}</span>
											</div>
										</v-alert>
									</div>

									<!-- Microsoft Token Scopes -->
									<div v-if="group.emailType === 'MICROSOFT' && group.tokenScopes && group.tokenScopes.length > 0" class="mb-4">
										<div class="text-subtitle-2 font-weight-bold mb-2">
											{{ $t('oauth2-debug-token-scopes') }}
										</div>

										<v-card outlined class="pa-3">
											<div class="d-flex flex-wrap">
												<v-chip
													v-for="scope in group.tokenScopes"
													:key="scope"
													small
													outlined
													color="blue"
													class="mr-2 mb-2"
												>
													{{ scope }}
												</v-chip>
											</div>
										</v-card>
									</div>

									<!-- Microsoft Token Scopes Error -->
									<div v-else-if="group.emailType === 'MICROSOFT' && (!group.tokenScopes || group.tokenScopes.length === 0)" class="mb-4">
										<div class="text-subtitle-2 font-weight-bold mb-2">
											{{ $t('oauth2-debug-token-scopes') }}
										</div>
										<v-alert
											type="warning"
											dense
											outlined
											icon="mdi-key-alert"
											class="mb-0"
										>
											<div class="d-flex align-center">
												<span>{{ $t('oauth2-debug-no-token-scopes') }}</span>
											</div>
										</v-alert>
									</div>

									<!-- Microsoft Subscriptions -->
									<div v-if="group.emailType === 'MICROSOFT' && group.subscriptions" class="mb-4">
										<div class="d-flex align-center justify-space-between mb-2">
											<div class="text-subtitle-2 font-weight-bold">
												{{ $t('oauth2-debug-subscriptions') }} ({{ getSubscriptionCount(group.subscriptions) }})
											</div>
											<v-btn
												small
												color="blue"
												outlined
												:loading="resubscribeLoading[group.id]"
												:disabled="anyResubscribeLoading"
												@click="forceResubscribe(group.id)"
											>
												<v-icon small left>mdi-refresh</v-icon>
												{{ $t('oauth2-debug-resubscribe') }}
											</v-btn>
										</div>

										<div v-if="getSubscriptionCount(group.subscriptions) === 0" class="grey--text text-body-2">
											{{ $t('oauth2-debug-no-active-subscriptions') }}
										</div>

										<v-card
											v-for="(subscription) in getSubscriptions(group.subscriptions)"
											:key="subscription.id"
											outlined
											class="mb-2 pa-3"
										>
											<div class="d-flex flex-wrap">
												<!-- Resource -->
												<div class="subscription-field mr-4 mb-2">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-resource') }}
													</div>
													<div class="text-body-2 font-weight-medium">
														{{ formatResource(subscription.resource) }}
													</div>
												</div>

												<!-- Change Type -->
												<div class="subscription-field mr-4 mb-2">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-change-type') }}
													</div>
													<v-chip
														x-small
														:color="getChangeTypeColor(subscription.changeType)"
														text-color="white"
													>
														{{ subscription.changeType }}
													</v-chip>
												</div>

												<!-- Expiration -->
												<div class="subscription-field mr-4 mb-2">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-expiration') }}
													</div>
													<div class="d-flex align-center">
														<v-icon
															x-small
															:color="isExpired(subscription.expirationDateTime) ? 'error' : 'success'"
															class="mr-1"
														>
															{{ isExpired(subscription.expirationDateTime) ? 'mdi-alert' : 'mdi-check' }}
														</v-icon>
														<span
															class="text-body-2"
															:class="{ 'error--text': isExpired(subscription.expirationDateTime) }"
														>
															{{ formatDateTime(subscription.expirationDateTime) }}
														</span>
													</div>
												</div>

												<!-- Notification URL -->
												<div class="subscription-field-full mb-2">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-notification-url') }}
													</div>
													<div class="text-body-2 text-truncate" style="max-width: 100%;">
														{{ subscription.notificationUrl }}
													</div>
												</div>

												<!-- Lifecycle URL -->
												<div class="subscription-field-full">
													<div class="text-caption grey--text">
														{{ $t('oauth2-debug-lifecycle-url') }}
													</div>
													<div class="text-body-2 text-truncate" style="max-width: 100%;">
														{{ subscription.lifecycleNotificationUrl || '—' }}
													</div>
												</div>
											</div>
										</v-card>
									</div>

									<!-- Microsoft Subscriptions Error -->
									<div v-else-if="group.emailType === 'MICROSOFT' && !group.subscriptions" class="mb-4">
										<div class="d-flex align-center justify-space-between mb-2">
											<div class="text-subtitle-2 font-weight-bold">
												{{ $t('oauth2-debug-subscriptions') }}
											</div>
											<v-btn
												small
												color="blue"
												outlined
												:loading="resubscribeLoading[group.id]"
												:disabled="anyResubscribeLoading"
												@click="forceResubscribe(group.id)"
											>
												<v-icon small left>mdi-refresh</v-icon>
												{{ $t('oauth2-debug-resubscribe') }}
											</v-btn>
										</div>
										<v-alert
											type="warning"
											dense
											outlined
											icon="mdi-bell-off"
											class="mb-0"
										>
											<div class="d-flex align-center">
												<span>{{ $t('oauth2-debug-no-subscriptions-data') }}</span>
											</div>
										</v-alert>
									</div>

									<!-- No type -->
									<div v-if="!group.oauth && group.emailType !== 'google' && group.emailType !== 'MICROSOFT'" class="grey--text text-body-2">
										{{ $t('oauth2-debug-no-additional-info') }}
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</div>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-end">
					<v-btn
						color="primary"
						class="px-4"
						@click="closeOAuth2DebugDialog"
					>
						{{ $t('close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Create Agent Dialog -->
		<v-dialog v-model="createAgentDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('create-agent') }}
					<v-spacer />
					<v-btn icon small @click="closeCreateAgentDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-form ref="createAgentForm" v-model="createAgentFormValid">
						<v-text-field
							v-model="createAgentData.organizationId"
							:label="$t('organization-id')"
							:rules="[rules.required]"
							outlined
							dense
							class="mb-2"
						/>
						<v-select
							v-model="createAgentData.erpType"
							:items="erpTypes"
							:label="$t('erp-type')"
							:rules="[rules.required]"
							outlined
							dense
							class="mb-2 text-capitalize"
						/>
						<v-text-field
							v-model="createAgentData.dbServer"
							:label="$t('db-server')"
							outlined
							dense
							class="mb-2"
						/>
						<v-switch
							v-model="createAgentData.enabled"
							:label="$t('enabled')"
							inset
							dense
							hide-details
							class="mt-0 mb-2"
						/>
						<v-switch
							v-model="createAgentData.downloadFile"
							:label="$t('download-installer')"
							inset
							dense
							hide-details
							class="mt-0"
						/>
					</v-form>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						@click="closeCreateAgentDialog"
					>
						{{ $t('cancel') }}
					</v-btn>

					<v-btn
						color="primary"
						:disabled="!createAgentFormValid"
						:loading="loading.createAgent"
						class="px-4"
						@click="createAgent"
					>
						{{ $t('create') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Create ERP Integration Dialog -->
		<v-dialog v-model="createErpIntegrationDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('create-erp-integration') }}
					<v-spacer />
					<v-btn icon small @click="closeCreateErpIntegrationDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-form ref="createErpIntegrationForm" v-model="createErpIntegrationFormValid">
						<v-text-field
							v-model="createErpIntegrationData.organizationId"
							:label="$t('organization-id')"
							:rules="[rules.required]"
							outlined
							dense
							class="mb-2"
						/>
						<v-select
							v-model="createErpIntegrationData.erpType"
							:items="erpTypes"
							:label="$t('erp-type')"
							:rules="[rules.required]"
							outlined
							dense
							class="mb-2 text-capitalize"
						/>
					</v-form>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						@click="closeCreateErpIntegrationDialog"
					>
						{{ $t('cancel') }}
					</v-btn>

					<v-btn
						color="primary"
						:disabled="!createErpIntegrationFormValid"
						:loading="loading.createErpIntegration"
						class="px-4"
						@click="createErpIntegration"
					>
						{{ $t('create') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Create Connector Dialog -->
		<v-dialog v-model="createConnectorDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('connector.create-action-title') }}
					<v-spacer />
					<v-btn icon small @click="closeCreateConnectorDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-form ref="createConnectorForm" v-model="createConnectorFormValid">
						<organization-search-select
							v-model="createConnectorData.organizationId"
							:organizations="connectorOrganizations"
							:label="$t('connector.organization')"
							:rules="[rules.required]"
							:loading="loadingConnectorOrganizations"
							class="mb-2"
						/>
						<v-select
							v-model="createConnectorData.type"
							:items="connectorTypes"
							item-value="type"
							item-text="label"
							:label="$t('connector.type')"
							:rules="[rules.required]"
							:loading="loadingConnectorTypes"
							:no-data-text="$t('no-data')"
							outlined
							dense
							class="mb-2"
						/>
						<v-text-field
							v-model="createConnectorData.crmId"
							:label="$t('connector.crm-id')"
							:rules="[rules.required]"
							outlined
							dense
							class="mb-2"
							@blur="trimConnectorField('crmId')"
						/>
						<v-text-field
							v-model="createConnectorData.username"
							:label="$t('connector.username')"
							:hint="$t('connector.username-hint')"
							persistent-hint
							outlined
							dense
							class="mb-3"
							@blur="trimConnectorField('username')"
						/>
						<v-text-field
							v-model="createConnectorData.password"
							:label="$t('connector.password')"
							:hint="$t('connector.password-hint')"
							persistent-hint
							type="password"
							outlined
							dense
							class="mb-2"
							@blur="trimConnectorField('password')"
						/>
					</v-form>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn color="error" class="px-4" @click="closeCreateConnectorDialog">
						{{ $t('cancel') }}
					</v-btn>

					<v-btn
						color="primary"
						:disabled="!createConnectorFormValid || loading.createConnector"
						:loading="loading.createConnector"
						class="px-4"
						@click="createConnector"
					>
						{{ $t('create') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Agent License Key Dialog -->
		<v-dialog v-model="licenseKeyDialog" max-width="500" content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('agent-license-key') }}
					<v-spacer />
					<v-btn icon small @click="licenseKeyDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-alert type="info" text dense class="mb-4">
						{{ $t('agent-license-key-info') }}
					</v-alert>
					<v-text-field
						v-if="agentLicenseAgentId"
						:value="agentLicenseAgentId"
						:label="$t('agent-id')"
						outlined
						dense
						readonly
						append-icon="mdi-content-copy"
						@click:append="copyToClipboard(agentLicenseAgentId)"
					/>
					<v-text-field
						v-model="agentLicenseKey"
						:label="$t('license-key')"
						outlined
						dense
						readonly
						append-icon="mdi-content-copy"
						@click:append="copyLicenseKey"
					/>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-end">
					<v-btn
						color="primary"
						class="px-4"
						@click="licenseKeyDialog = false"
					>
						{{ $t('close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Twilio Recordings Dialog -->
		<v-dialog
			v-model="twilioRecordingsDialog"
			max-width="600"
			persistent
			:fullscreen="$vuetify.breakpoint.xs"
			content-class="devops-subdialog"
		>
			<v-card class="twilio-recordings-card">
				<v-card-title class="text-h6 font-weight-bold py-4 px-4 d-flex align-center">
					<v-icon color="orange darken-2" class="mr-2">mdi-microphone</v-icon>
					{{ $t('twilio-recordings') }}
					<v-spacer />
					<v-btn icon small @click="closeTwilioRecordingsDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="mx-4" />

				<v-card-text class="py-4">
					<v-form ref="twilioRecordingsForm" v-model="twilioRecordingsFormValid">
						<!-- Date Range Section -->
						<div class="text-subtitle-2 font-weight-bold mb-3 d-flex align-center">
							<v-icon small color="primary" class="mr-2">mdi-calendar-range</v-icon>
							{{ $t('twilio-recordings-date-range') }}
						</div>

						<v-row dense>
							<v-col cols="12" sm="6">
								<v-menu
									ref="startDateMenu"
									v-model="startDateMenu"
									:close-on-content-click="false"
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template #activator="{ on, attrs }">
										<v-text-field
											v-model="twilioRecordingsData.startDate"
											:label="$t('twilio-recordings-start-date')"
											:rules="[rules.required]"
											outlined
											dense
											readonly
											hide-details="auto"
											prepend-inner-icon="mdi-calendar"
											v-bind="attrs"
											v-on="on"
										/>
									</template>
									<v-date-picker
										v-model="twilioRecordingsData.startDate"
										:max="twilioRecordingsData.endDate || today"
										no-title
										scrollable
										@input="startDateMenu = false"
									/>
								</v-menu>
							</v-col>
							<v-col cols="12" sm="6">
								<v-text-field
									v-model="twilioRecordingsData.startTime"
									:label="$t('twilio-recordings-start-time')"
									:rules="[rules.required]"
									outlined
									dense
									hide-details="auto"
									type="time"
									prepend-inner-icon="mdi-clock-outline"
								/>
							</v-col>
						</v-row>

						<v-row dense class="mt-1">
							<v-col cols="12" sm="6">
								<v-menu
									ref="endDateMenu"
									v-model="endDateMenu"
									:close-on-content-click="false"
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template #activator="{ on, attrs }">
										<v-text-field
											v-model="twilioRecordingsData.endDate"
											:label="$t('twilio-recordings-end-date')"
											:rules="[rules.required]"
											outlined
											dense
											readonly
											hide-details="auto"
											prepend-inner-icon="mdi-calendar"
											v-bind="attrs"
											v-on="on"
										/>
									</template>
									<v-date-picker
										v-model="twilioRecordingsData.endDate"
										:min="twilioRecordingsData.startDate"
										:max="today"
										no-title
										scrollable
										@input="endDateMenu = false"
									/>
								</v-menu>
							</v-col>
							<v-col cols="12" sm="6">
								<v-text-field
									v-model="twilioRecordingsData.endTime"
									:label="$t('twilio-recordings-end-time')"
									:rules="[rules.required]"
									outlined
									dense
									type="time"
									prepend-inner-icon="mdi-clock-outline"
								/>
							</v-col>
						</v-row>

						<!-- Timezone Section -->
						<div class="text-subtitle-2 font-weight-bold mt-1 mb-2 d-flex align-center">
							<v-icon small color="primary" class="mr-2">mdi-earth</v-icon>
							{{ $t('twilio-recordings-timezone') }}
						</div>

						<v-select
							v-model="twilioRecordingsData.timezone"
							:items="timezoneOptions"
							:label="$t('twilio-recordings-select-timezone')"
							outlined
							dense
							prepend-inner-icon="mdi-map-clock"
							item-text="label"
							item-value="value"
						>
							<template #item="{ item }">
								<v-list-item-content>
									<v-list-item-title>{{ item.label }}</v-list-item-title>
									<v-list-item-subtitle class="text-caption">
										{{ item.value }}
									</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-select>
					</v-form>

					<!-- Results Section -->
					<div v-if="twilioRecordingsResults" class="mt-4">
						<v-divider class="mb-4" />
						<div class="d-flex align-center justify-space-between mb-2">
							<div class="text-subtitle-2 font-weight-bold d-flex align-center">
								<v-icon small color="success" class="mr-2">mdi-playlist-music</v-icon>
								{{ $t('twilio-recordings-results') }}
							</div>
							<div class="d-flex align-center">
								<v-btn
									small
									color="orange darken-2"
									outlined
									class="mr-2"
									:loading="downloadingAllRecordings"
									:disabled="filteredTwilioRecordings.length === 0 || downloadingAllRecordings"
									@click="downloadAllRecordings"
								>
									<v-icon left small>mdi-download-multiple</v-icon>
									{{ $t('twilio-recordings-download-all') }}
								</v-btn>
								<v-chip small color="primary" text-color="white">
									{{ filteredTwilioRecordings.length }} / {{ twilioRecordingsResults.length }}
								</v-chip>
							</div>
						</div>

						<v-text-field
							v-model="twilioRecordingsSearch"
							:placeholder="$t('twilio-recordings-search-phone')"
							prepend-inner-icon="mdi-magnify"
							dense
							outlined
							hide-details
							clearable
							class="mb-2"
						/>

						<div v-if="filteredTwilioRecordings.length === 0" class="text-center py-4">
							<v-icon size="48" color="grey lighten-1">mdi-microphone-off</v-icon>
							<div class="text-body-2 grey--text mt-2">
								{{ $t('twilio-recordings-no-results') }}
							</div>
						</div>

						<div
							v-else
							class="recordings-list"
						>
							<div
								v-for="item in filteredTwilioRecordings"
								:key="item.sid"
								class="recording-item d-flex align-center py-2 px-3"
							>
								<v-btn
									icon
									small
									color="orange darken-2"
									class="mr-2 flex-shrink-0"
									@click="downloadRecording(item)"
								>
									<v-icon small>mdi-download</v-icon>
								</v-btn>
								<div class="flex-grow-1 min-width-0 overflow-hidden">
									<div class="d-flex align-center">
										<span class="text-body-2 text-truncate">{{ item.dateCreated }}</span>
										<span class="text-caption grey--text ml-2 flex-shrink-0">{{ formatDuration(item.duration) }}</span>
									</div>
									<div class="text-caption grey--text d-flex align-center" style="max-width: 100%;">
										<span v-if="item.from" class="text-truncate" style="max-width: 45%;">{{ item.from }}</span>
										<v-icon x-small class="mx-1 flex-shrink-0">mdi-arrow-right</v-icon>
										<span v-if="item.to" class="text-truncate" style="max-width: 45%;">{{ item.to }}</span>
									</div>
								</div>
								<v-chip x-small :color="getRecordingStatusColor(item.status)" text-color="white" class="ml-2 flex-shrink-0">
									{{ item.status }}
								</v-chip>
							</div>
						</div>
					</div>
				</v-card-text>

				<v-card-actions class="px-4 py-4 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						@click="closeTwilioRecordingsDialog"
					>
						{{ twilioRecordingsResults ? $t('close') : $t('cancel') }}
					</v-btn>

					<v-btn
						color="primary"
						:disabled="!twilioRecordingsFormValid"
						:loading="loading.twilioRecordings"
						class="px-4"
						@click="fetchTwilioRecordings"
					>
						<v-icon left small>mdi-magnify</v-icon>
						{{ $t('twilio-recordings-search') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Verified Caller ID Dialog -->
		<v-dialog v-model="callerIdDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('caller-id-title') }}
					<v-spacer />
					<v-btn icon small @click="closeCallerIdDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<!-- Step 1: Phone input form -->
					<div v-if="!callerIdResult">
						<v-form ref="callerIdForm" v-model="callerIdFormValid" @submit.prevent>
							<v-text-field
								v-model="callerIdPhone"
								:label="$t('caller-id-phone')"
								:rules="[rules.required, rules.e164Phone]"
								outlined
								dense
								placeholder="+15551234567"
								hide-details="auto"
								class="mb-3"
							/>
							<v-text-field
								v-model="callerIdExtension"
								:label="$t('caller-id-extension')"
								outlined
								dense
								hide-details="auto"
								class="mb-1"
							/>
							<div class="text-caption grey--text mb-3 ml-1">
								{{ $t('caller-id-extension-hint') }}
							</div>
							<v-text-field
								v-model="callerIdFriendlyName"
								:label="$t('caller-id-friendly-name')"
								outlined
								dense
								readonly
								hide-details="auto"
							/>
						</v-form>
					</div>

					<!-- Step 2: Validation code & status -->
					<div v-else>
						<!-- Validation Code -->
						<div class="text-body-2 text-center mb-3">
							{{ $t('caller-id-code-instructions') }}
						</div>
						<div class="caller-id-code-box mb-4">
							<span
								v-for="(digit, i) in String(callerIdResult.validationCode).split('')"
								:key="i"
								class="caller-id-digit"
							>
								{{ digit }}
							</span>
						</div>

						<!-- Status -->
						<div class="d-flex align-center justify-center">
							<template v-if="callerIdStatus === 'pending'">
								<v-progress-circular
									indeterminate
									color="primary"
									size="24"
									class="mr-2"
								/>
								<span class="text-body-1">{{ $t('caller-id-status-pending') }}</span>
							</template>

							<template v-else-if="callerIdStatus === 'success'">
								<v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
								<span class="text-body-1 success--text font-weight-bold">
									{{ $t('caller-id-status-success') }}
								</span>
							</template>

							<template v-else-if="callerIdStatus === 'failed'">
								<v-icon color="error" class="mr-2">mdi-close-circle</v-icon>
								<span class="text-body-1 error--text font-weight-bold">
									{{ $t('caller-id-status-failed') }}
								</span>
							</template>

							<template v-else-if="callerIdStatus === 'expired'">
								<v-icon color="warning" class="mr-2">mdi-clock-alert</v-icon>
								<span class="text-body-1 warning--text font-weight-bold">
									{{ $t('caller-id-status-expired') }}
								</span>
							</template>
						</div>

						<div v-if="callerIdStatus === 'failed' || callerIdStatus === 'expired'" class="d-flex justify-center mt-4">
							<v-btn
								color="primary"
								:loading="loading.callerIdValidation"
								@click="retryCallerIdValidation"
							>
								<v-icon left small>mdi-refresh</v-icon>
								{{ $t('caller-id-try-again') }}
							</v-btn>
						</div>
					</div>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						@click="closeCallerIdDialog"
					>
						{{ callerIdResult ? $t('close') : $t('cancel') }}
					</v-btn>

					<v-btn
						v-if="!callerIdResult"
						color="primary"
						:disabled="!callerIdFormValid"
						:loading="loading.callerIdValidation"
						class="px-4"
						@click="submitCallerIdValidation"
					>
						{{ $t('caller-id-validate') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Transfer Phone Number Dialog -->
		<v-dialog v-model="transferPhoneDialog" max-width="550" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t('transfer-phone-number') }}
					<v-spacer />
					<v-btn icon small @click="closeTransferPhoneDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-divider class="my-4 mx-4" />

				<v-card-text class="pb-2">
					<v-form ref="transferPhoneForm" v-model="transferPhoneFormValid">
						<v-autocomplete
							v-model="transferPhoneData.sourceAccountSid"
							:items="transferPhoneAccountOptions"
							:label="$t('transfer-phone-source-account')"
							:rules="[rules.required]"
							:loading="loadingTransferPhoneAccounts"
							outlined
							dense
							item-text="label"
							item-value="accountSid"
							class="mb-2"
							hide-details="auto"
							@change="onSourceAccountChange"
						>
							<template #item="{ item }">
								<v-list-item-content>
									<v-list-item-title>{{ item.label }}</v-list-item-title>
									<v-list-item-subtitle>{{ item.accountSid }}</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-autocomplete>

						<v-autocomplete
							v-model="transferPhoneData.destinationAccountSid"
							:items="transferPhoneAccountOptions"
							:label="$t('transfer-phone-destination-account')"
							:rules="[rules.required]"
							:loading="loadingTransferPhoneAccounts"
							outlined
							dense
							item-text="label"
							item-value="accountSid"
							class="mb-2"
							hide-details="auto"
							@change="onDestinationAccountChange"
						>
							<template #item="{ item }">
								<v-list-item-content>
									<v-list-item-title>{{ item.label }}</v-list-item-title>
									<v-list-item-subtitle>{{ item.accountSid }}</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-autocomplete>

						<v-autocomplete
							v-model="transferPhoneData.addressSid"
							:items="transferPhoneAddressOptions"
							:label="$t('transfer-phone-destination-address')"
							:loading="loadingTransferPhoneAddresses"
							:disabled="!transferPhoneData.destinationAccountSid"
							outlined
							dense
							item-text="label"
							item-value="sid"
							class="mb-2"
							hide-details="auto"
							clearable
						>
							<template #item="{ item }">
								<v-list-item-content>
									<v-list-item-title>{{ item.label }}</v-list-item-title>
									<v-list-item-subtitle>{{ item.sid }}</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-autocomplete>

						<v-autocomplete
							v-model="transferPhoneData.phoneNumberSid"
							:items="transferPhoneNumberOptions"
							:label="$t('transfer-phone-source-phone-number')"
							:rules="[rules.required]"
							:loading="loadingTransferPhoneNumbers"
							:disabled="!transferPhoneData.sourceAccountSid"
							outlined
							dense
							item-text="label"
							item-value="sid"
							hide-details
						>
							<template #item="{ item }">
								<v-list-item-content>
									<v-list-item-title>{{ item.label }}</v-list-item-title>
									<v-list-item-subtitle>{{ item.sid }}</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-autocomplete>
					</v-form>

					<v-alert
						type="info"
						dense
						outlined
						icon="mdi-clock-outline"
						class="mt-3 mb-0 transfer-phone-alert"
					>
						{{ $t('transfer-phone-emergency-address-warning') }}
					</v-alert>
				</v-card-text>

				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn
						color="error"
						class="px-4"
						@click="closeTransferPhoneDialog"
					>
						{{ $t('cancel') }}
					</v-btn>

					<v-btn
						color="primary"
						:disabled="!transferPhoneFormValid || transferPhoneData.sourceAccountSid === transferPhoneData.destinationAccountSid"
						:loading="loading.transferPhone"
						class="px-4"
						@click="confirmTransferPhone"
					>
						{{ $t('confirm') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import { RequiredValidator, PhoneValidator } from "@/validators";
import { mapGetters, mapActions } from "vuex";
import ConfirmationDialog from "@/components/configuration/ConfirmationDialog.vue";
import OrganizationSearchSelect from "@/components/support/OrganizationSearchSelect.vue";
import connectorDevopsService from "@/services/connectorDevopsService";

const PORTABILITY_API_DOCS_URL = "https://www.twilio.com/docs/phone-numbers/port-in/portability-api#not-portable-reason-codes";
const HOSTED_NUMBERS_DOCS_URL = "https://www.twilio.com/docs/phone-numbers/hosted-numbers/hosted-numbers-api/eligibility-resource#response-properties";

export default {
	name: "DevopsActions",

	components: {
		ConfirmationDialog,
		OrganizationSearchSelect
	},

	props: {
		embedded: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			PORTABILITY_API_DOCS_URL,
			HOSTED_NUMBERS_DOCS_URL,
			loading: {
				purge: false,
				reindexCurrent: false,
				reindexAll: false,
				migrate: false,
				portabilityCheck: false,
				oauth2Debug: false,
				createAgent: false,
				createErpIntegration: false,
				avancieTenants: false,
				twilioRecordings: false,
				callerIdValidation: false,
				transferPhone: false,
				createConnector: false,
				orphanReport: false,
				phoneWebhooks: false
			},
			resubscribeLoading: {},
			deleteCredentialLoading: {},
			migrateDialog: false,
			migrateFormValid: false,
			migrateData: {
				from: "",
				to: ""
			},
			portabilityDialog: false,
			portabilityFormValid: false,
			portabilityPhone: "",
			portabilityResult: null,
			oauth2DebugDialog: false,
			oauth2DebugResult: null,
			userGroups: [],
			allPhones: [],
			loadingGroups: false,
			loadingFromPhones: false,
			createAgentDialog: false,
			createAgentFormValid: false,
			createAgentData: {
				organizationId: "",
				erpType: "",
				dbServer: "",
				enabled: true,
				downloadFile: true
			},
			erpTypes: [],
			createErpIntegrationDialog: false,
			createErpIntegrationFormValid: false,
			createErpIntegrationData: {
				organizationId: "",
				erpType: ""
			},
			createConnectorDialog: false,
			createConnectorFormValid: false,
			createConnectorData: {
				organizationId: "",
				type: "",
				crmId: "",
				username: "",
				password: ""
			},
			connectorTypes: [],
			loadingConnectorTypes: false,
			connectorOrganizations: [],
			loadingConnectorOrganizations: false,
			licenseKeyDialog: false,
			agentLicenseKey: "",
			agentLicenseAgentId: "",
			currentOrgIntegrations: [],
			avancieTenants: null,
			actionsMenuOpen: false,
			twilioRecordingsDialog: false,
			twilioRecordingsFormValid: false,
			twilioRecordingsData: {
				startDate: "",
				startTime: "00:00",
				endDate: "",
				endTime: "23:59",
				timezone: "America/Montreal"
			},
			twilioRecordingsResults: null,
			twilioRecordingsSearch: "",
			downloadingAllRecordings: false,
			startDateMenu: false,
			endDateMenu: false,
			rules: {
				required: RequiredValidator,
				phone: PhoneValidator,
				e164Phone: v => /^\+[1-9]\d{1,14}$/.test(v) || this.$t("error.validator.phone-invalid")
			},
			callerIdDialog: false,
			callerIdFormValid: false,
			callerIdPhone: "",
			callerIdExtension: "",
			callerIdFriendlyName: "",
			callerIdResult: null,
			callerIdStatus: null,
			callerIdPollingTimer: null,
			transferPhoneDialog: false,
			transferPhoneFormValid: false,
			transferPhoneData: {
				sourceAccountSid: "",
				destinationAccountSid: "",
				phoneNumberSid: "",
				addressSid: ""
			},
			transferPhoneAccounts: null,
			transferPhoneNumbers: [],
			transferPhoneAddresses: [],
			loadingTransferPhoneAccounts: false,
			loadingTransferPhoneNumbers: false,
			loadingTransferPhoneAddresses: false,
			phoneWebhooksDialog: false,
			phoneWebhooksAllOrgs: false,
			phoneWebhooksResult: null,
			repairingWebhooks: false,
			orphanReportDialog: false,
			orphanReport: null,
			orphanCategories: [],
			orphanCategoryResults: {},
			loadingCategory: null,
			orphanPreviewLimit: 12,
			selectedOrphanCollection: null,
			selectedOrphanMissingType: null,
			orphanGhostOrgExpanded: false,
			orphanFindingsExpanded: false
		};
	},

	computed: {
		...mapGetters("login", ["profile"]),
		...mapGetters("clinic", ["clinicData"]),

		availableActions() {
			return [
				{
					key: "purge",
					title: this.$t("purge-all-contacts"),
					description: this.$t("purge-all-contacts-description"),
					icon: "mdi-delete-sweep",
					color: "error",
					loadingKey: "purge",
					handler: () => this.confirmAction("purge")
				},
				{
					key: "reindexCurrent",
					title: this.$t("reindex-contacts-current"),
					description: this.$t("reindex-contacts-current-description"),
					icon: "mdi-database-refresh",
					color: "primary",
					loadingKey: "reindexCurrent",
					handler: () => this.confirmAction("reindexCurrent")
				},
				{
					key: "reindexAll",
					title: this.$t("reindex-contacts-all"),
					description: this.$t("reindex-contacts-all-description"),
					icon: "mdi-database-sync",
					color: "warning darken-2",
					loadingKey: "reindexAll",
					handler: () => this.confirmAction("reindexAll")
				},
				{
					key: "migrate",
					title: this.$t("migrate-conversations"),
					description: this.$t("migrate-conversations-description"),
					icon: "mdi-swap-horizontal",
					color: "info",
					loadingKey: "migrate",
					handler: () => this.openMigrateDialog()
				},
				{
					key: "portabilityCheck",
					title: this.$t("portability-check"),
					description: this.$t("portability-check-description"),
					icon: "mdi-phone-check",
					color: "purple",
					loadingKey: "portabilityCheck",
					handler: () => this.openPortabilityDialog()
				},
				{
					key: "oauth2Debug",
					title: this.$t("oauth2-debug"),
					description: this.$t("oauth2-debug-description"),
					icon: "mdi-shield-key",
					color: "teal",
					loadingKey: "oauth2Debug",
					handler: () => this.openOAuth2DebugDialog()
				},
				{
					key: "createAgent",
					title: this.$t("create-agent"),
					description: this.$t("create-agent-description"),
					icon: "mdi-plus-circle",
					color: "success",
					loadingKey: "createAgent",
					handler: () => this.openCreateAgentDialog()
				},
				{
					key: "createErpIntegration",
					title: this.$t("create-erp-integration"),
					description: this.$t("create-erp-integration-description"),
					icon: "mdi-database-plus",
					color: "teal",
					loadingKey: "createErpIntegration",
					handler: () => this.openCreateErpIntegrationDialog()
				},
				{
					key: "createConnector",
					title: this.$t("connector.create-action-title"),
					description: this.$t("connector.create-action-description"),
					icon: "mdi-connection",
					color: "success",
					loadingKey: "createConnector",
					handler: () => this.openCreateConnectorDialog()
				},
				{
					key: "avancieTenants",
					title: this.$t("get-avancie-tenants"),
					description: this.$t("get-avancie-tenants-description"),
					icon: "mdi-office-building",
					color: "deep-purple",
					loadingKey: "avancieTenants",
					handler: () => this.fetchAvancieTenants()
				},
				{
					key: "orphanReport",
					title: this.$t("orphan-report-title"),
					description: this.$t("orphan-report-description"),
					icon: "mdi-file-search-outline",
					color: "red darken-1",
					loadingKey: "orphanReport",
					handler: () => this.openOrphanReportDialog()
				},
				{
					key: "twilioRecordings",
					title: this.$t("twilio-recordings"),
					description: this.$t("twilio-recordings-description"),
					icon: "mdi-microphone",
					color: "orange darken-2",
					loadingKey: "twilioRecordings",
					handler: () => this.openTwilioRecordingsDialog()
				},
				{
					key: "callerIdValidation",
					title: this.$t("caller-id-title"),
					description: this.$t("caller-id-description"),
					icon: "mdi-phone-check-outline",
					color: "green",
					loadingKey: "callerIdValidation",
					handler: () => this.openCallerIdDialog()
				},
				{
					key: "transferPhone",
					title: this.$t("transfer-phone-number"),
					description: this.$t("transfer-phone-number-description"),
					icon: "mdi-phone-forward",
					color: "indigo",
					loadingKey: "transferPhone",
					handler: () => this.openTransferPhoneDialog()
				},
				{
					key: "phoneWebhooks",
					title: this.$t("phone-webhooks"),
					description: this.$t("phone-webhooks-description"),
					icon: "mdi-webhook",
					color: "cyan darken-2",
					loadingKey: "phoneWebhooks",
					handler: () => this.openPhoneWebhooksDialog()
				}
			];
		},

		anyLoading() {
			return Object.values(this.loading).some(v => v);
		},

		anyResubscribeLoading() {
			return Object.values(this.resubscribeLoading).some(v => v);
		},

		hasAvancieIntegration() {
			return this.currentOrgIntegrations.some(integration => integration.erpType === "avancie");
		},

		transferPhoneAccountOptions() {
			if (!this.transferPhoneAccounts) return [];

			const options = [];

			if (this.transferPhoneAccounts.mainAccount) {
				options.push({
					label: `${this.transferPhoneAccounts.mainAccount.name} (${this.$t("transfer-phone-main-account")})`,
					accountSid: this.transferPhoneAccounts.mainAccount.accountSid
				});
			}

			const sortedOrgs = [...(this.transferPhoneAccounts.organizations || [])]
				.sort((a, b) => {
					const nameA = this.getOrgDisplayName(a.name);
					const nameB = this.getOrgDisplayName(b.name);

					return nameA.localeCompare(nameB);
				});

			for (const org of sortedOrgs) {
				options.push({
					label: `${this.getOrgDisplayName(org.name)} (${org.id})`,
					accountSid: org.accountSid
				});
			}

			return options;
		},

		transferPhoneNumberOptions() {
			return this.transferPhoneNumbers.map(pn => ({
				label: pn.friendlyName || pn.phoneNumber,
				phoneNumber: pn.phoneNumber,
				sid: pn.sid
			}));
		},

		transferPhoneAddressOptions() {
			return this.transferPhoneAddresses.map(addr => ({
				label: [addr.customerName || addr.friendlyName, addr.street, addr.city, addr.region, addr.postalCode].filter(Boolean).join(", "),
				sid: addr.sid
			}));
		},

		filteredTwilioRecordings() {
			if (!this.twilioRecordingsResults) return [];
			if (!this.twilioRecordingsSearch) return this.twilioRecordingsResults;

			const search = this.twilioRecordingsSearch.toLowerCase();

			return this.twilioRecordingsResults.filter(recording => {
				const from = (recording.from || "").toLowerCase();
				const to = (recording.to || "").toLowerCase();

				return from.includes(search) || to.includes(search);
			});
		},

		organizationId() {
			return this.profile?.clinic;
		},

		organizationName() {
			if (!this.clinicData?.name) {
				return;
			}

			const name = this.clinicData.name;

			if (typeof name === "object") {
				// Try current locale first, then fall back to first available
				const locale = this.$i18n?.locale || "fr-FR";

				return name[locale] || name["fr-FR"] || Object.values(name)[0] || "—";
			}

			return name;
		},

		avancieTenantsResults() {
			if (!this.avancieTenants) return [];

			// Handle direct array response
			if (Array.isArray(this.avancieTenants)) {
				return this.avancieTenants;
			}

			// Handle { count, results: { data: { tenants: [...] } } } format (actual API response)
			if (this.avancieTenants.results?.data?.tenants) {
				return this.avancieTenants.results.data.tenants;
			}

			// Handle { data: { tenants: [...] } } format
			if (this.avancieTenants.data?.tenants) {
				return this.avancieTenants.data.tenants;
			}

			// Handle { tenants: [...] } format
			if (this.avancieTenants.tenants) {
				return this.avancieTenants.tenants;
			}

			// Handle { results: [...] } format
			if (Array.isArray(this.avancieTenants.results)) {
				return this.avancieTenants.results;
			}

			return [];
		},

		orphanCollectionsSummary() {
			const perCollection = this.orphanReport?.summary?.perCollection;

			if (!perCollection) {
				return [];
			}

			return Object.entries(perCollection)
				.map(([collection, count]) => ({ collection, count }))
				.sort((a, b) => b.count - a.count);
		},

		orphanFindingsPreview() {
			if (!Array.isArray(this.orphanReport?.findings)) {
				return [];
			}

			return this.orphanReport.findings.slice(0, this.orphanPreviewLimit);
		},

		hasOrphanResults() {
			return Object.keys(this.orphanCategoryResults).length > 0;
		},

		orphanByMissingReference() {
			if (!Array.isArray(this.orphanReport?.findings)) {
				return [];
			}

			const grouped = {};
			const refTypes = ["organization", "user-group", "voicemail", "organization-mismatch"];

			refTypes.forEach(type => {
				grouped[type] = {};
			});

			this.orphanReport.findings.forEach(finding => {
				const refType = finding.missingReference || "unknown";

				if (!grouped[refType]) {
					grouped[refType] = {};
				}

				const collection = finding.collection;

				if (!grouped[refType][collection]) {
					grouped[refType][collection] = 0;
				}

				grouped[refType][collection]++;
			});

			return refTypes
				.filter(type => Object.keys(grouped[type]).length > 0)
				.map(type => ({
					type,
					total: Object.values(grouped[type]).reduce((sum, count) => sum + count, 0),
					columns: Object.entries(grouped[type])
						.map(([collection, count]) => ({ collection, count }))
						.sort((a, b) => b.count - a.count)
				}));
		},

		uniqueGhostOrganizations() {
			if (!Array.isArray(this.orphanReport?.findings)) {
				return [];
			}

			const orgIds = new Set();

			this.orphanReport.findings
				.filter(f => f.missingReference === "organization" && f.organizationId)
				.forEach(f => orgIds.add(f.organizationId));

			return Array.from(orgIds).sort();
		},

		selectedCollectionFindings() {
			if (!this.selectedOrphanCollection || !this.selectedOrphanMissingType) {
				return [];
			}

			return this.orphanReport?.findings?.filter(f =>
				f.collection === this.selectedOrphanCollection && f.missingReference === this.selectedOrphanMissingType
			) || [];
		},

		selectedCollectionFindingsGrouped() {
			const findings = this.selectedCollectionFindings;
			const grouped = {};

			findings.forEach(finding => {
				const orgId = finding.organizationId || "unknown";

				if (!grouped[orgId]) {
					grouped[orgId] = [];
				}
				grouped[orgId].push(finding);
			});

			return Object.entries(grouped)
				.map(([orgId, docs]) => ({
					organizationId: orgId,
					documents: docs.sort((a, b) => a.documentId.localeCompare(b.documentId))
				}))
				.sort((a, b) => a.organizationId.localeCompare(b.organizationId));
		},

		fromPhoneNumberOptions() {
			const options = [];

			// Create a lookup map for group names by ID
			const groupNameMap = {};

			this.userGroups.forEach(group => {
				groupNameMap[group.id] = group.label;
			});

			this.allPhones.forEach(org => {
				if (org.numbers) {
					org.numbers.forEach(num => {
						const isUnassigned = num.status === "no-group" || !num.group;
						const groupName = num.group ? groupNameMap[num.group] : null;

						let typeLabel = "";

						if (isUnassigned) {
							typeLabel = this.$t("unassigned");
						} else if (num.voice && num.sms) {
							typeLabel = `${this.$t("voice")} + ${this.$t("sms")}`;
						} else if (num.voice) {
							typeLabel = this.$t("voice");
						} else if (num.sms) {
							typeLabel = this.$t("sms");
						}

						const label = isUnassigned
							? `${this.$t("unassigned")} (${typeLabel})`
							: `${groupName || num.group} (${typeLabel})`;

						options.push({
							label,
							phoneNumber: num.number,
							groupId: num.group,
							groupName: groupName || this.$t("unassigned"),
							type: typeLabel,
							isUnassigned
						});
					});
				}
			});

			return options;
		},

		toPhoneNumberOptions() {
			const options = [];

			this.userGroups.forEach(group => {
				if (group.phoneNumber) {
					options.push({
						label: `${group.label} (${this.$t("voice")})`,
						phoneNumber: group.phoneNumber,
						groupId: group.id
					});
				}

				if (group.phoneNumberSMS) {
					options.push({
						label: `${group.label} (${this.$t("sms")})`,
						phoneNumber: group.phoneNumberSMS,
						groupId: group.id
					});
				}
			});

			return options;
		},

		folderNames() {
			return {
				inbox: `📥 ${this.$t("oauth2-debug-folder-inbox")}`,
				sentitems: `📤 ${this.$t("oauth2-debug-folder-sent")}`,
				drafts: `📝 ${this.$t("oauth2-debug-folder-drafts")}`,
				deleteditems: `🗑️ ${this.$t("oauth2-debug-folder-deleted")}`,
				junkemail: `🚫 ${this.$t("oauth2-debug-folder-junk")}`
			};
		},

		today() {
			return new Date().toISOString().substr(0, 10);
		},

		timezoneOptions() {
			return [
				{ label: "🇨🇦 " + this.$t("timezone-montreal"), value: "America/Montreal" },
				{ label: "🇨🇦 " + this.$t("timezone-toronto"), value: "America/Toronto" },
				{ label: "🇨🇦 " + this.$t("timezone-vancouver"), value: "America/Vancouver" },
				{ label: "🇺🇸 " + this.$t("timezone-new-york"), value: "America/New_York" },
				{ label: "🇺🇸 " + this.$t("timezone-chicago"), value: "America/Chicago" },
				{ label: "🇺🇸 " + this.$t("timezone-denver"), value: "America/Denver" },
				{ label: "🇺🇸 " + this.$t("timezone-los-angeles"), value: "America/Los_Angeles" },
				{ label: "🇬🇧 " + this.$t("timezone-london"), value: "Europe/London" },
				{ label: "🇫🇷 " + this.$t("timezone-paris"), value: "Europe/Paris" },
				{ label: "🌍 " + this.$t("timezone-utc"), value: "UTC" }
			];
		}
	},

	watch: {
		actionsMenuOpen(isOpen) {
			if (isOpen) {
				// Need a small delay for the menu to fully render in the DOM
				setTimeout(() => {
					this.scrollToTop();
				}, 50);
			}
		},

		portabilityPhone(newVal, oldVal) {
			if (!newVal) return;

			// Remove dashes, parentheses, and spaces
			let cleaned = newVal.replace(/[-() ]/g, "");

			// If doesn't start with +, add +1
			if (cleaned && !cleaned.startsWith("+")) {
				cleaned = "+1" + cleaned;
			}

			// Only update if different to avoid infinite loop
			if (cleaned !== newVal) {
				this.portabilityPhone = cleaned;
			}
		},

		callerIdPhone(newVal) {
			if (!newVal) {
				this.callerIdFriendlyName = "";
				return;
			}

			// Strip everything except digits and leading +
			let cleaned = newVal.replace(/[^\d+]/g, "");

			// If it's just digits (no +), prepend +1
			if (cleaned && !cleaned.startsWith("+")) {
				cleaned = "+1" + cleaned;
			}

			if (cleaned !== newVal) {
				this.callerIdPhone = cleaned;
				return;
			}

			this.callerIdFriendlyName = this.formatE164ToDisplay(cleaned);
		}
	},

	async created() {
		await Promise.all([
			this.fetchErpTypes(),
			this.fetchCurrentOrgIntegration()
		]);
	},

	beforeDestroy() {
		this.stopCallerIdPolling();
	},

	methods: {
		...mapActions("groups", ["getAllGroups"]),

		scrollToTop() {
			// For non-embedded mode (menu card) - query the DOM for the actions-menu-card
			const menuCard = document.querySelector(".actions-menu-card");

			if (menuCard) {
				menuCard.scrollTop = 0;
			}
			// For embedded mode (direct list)
			if (this.$refs.embeddedList) {
				this.$refs.embeddedList.scrollTop = 0;
			}
		},

		executeAction(action) {
			if (action?.handler) {
				action.handler();
			}
		},

		async fetchErpTypes() {
			try {
				const { data } = await this.axios.get("/devops/erp-types");

				this.erpTypes = data;
			} catch (err) {
				console.error("Error fetching ERP types:", err);
			}
		},

		async fetchCurrentOrgIntegration() {
			try {
				const { data } = await this.axios.get("/devops/erp-integrations/current");

				this.currentOrgIntegrations = data;
			} catch (err) {
				// Not all orgs have integrations, so this might 404 - ignore
				console.log("No current org integration found");
			}
		},

		async fetchAvancieTenants() {
			this.loading.avancieTenants = true;
			this.avancieTenants = null;
			try {
				const { data } = await this.axios.get("/patients/integrations/avancie/tenants");

				console.log("Avancie tenants response:", data);
				this.avancieTenants = data;
				console.log("avancieTenantsResults:", this.avancieTenantsResults);
			} catch (err) {
				console.error("Error fetching Avancie tenants:", err);
				this.$error(this.$t("error.fetch-avancie-tenants"));
			} finally {
				this.loading.avancieTenants = false;
			}
		},

		openOrphanReportDialog() {
			this.orphanReportDialog = true;
			this.orphanReport = null;
			this.orphanCategoryResults = {};
			this.selectedOrphanCollection = null;
			this.selectedOrphanMissingType = null;
			this.fetchOrphanCategories();
		},

		closeOrphanReportDialog() {
			this.orphanReportDialog = false;
			this.selectedOrphanCollection = null;
			this.selectedOrphanMissingType = null;
			this.loadingCategory = null;
			this.orphanCategoryResults = {};
			this.orphanReport = null;
		},

		selectOrphanCollection(collection, missingType) {
			this.selectedOrphanCollection = collection;
			this.selectedOrphanMissingType = missingType;

			// Scroll the selected collection panel into view
			this.$nextTick(() => {
				const panel = this.$refs.selectedCollectionPanel;

				if (panel) {
					panel.scrollIntoView({ behavior: "smooth", block: "start" });
				}
			});
		},

		async fetchOrphanCategories() {
			try {
				const { data } = await this.axios.get("/devops/orphans/categories");

				this.orphanCategories = Array.isArray(data) ? data : [];
			} catch (err) {
				this.$error(
					err.response?.data?.message || this.$t("error.orphan-report")
				);
			}
		},

		// Scan a single category and merge its findings into the report. Scanning one
		// category at a time keeps each request small enough to avoid timeouts.
		async fetchOrphanReport(category) {
			if (!category) {
				return;
			}

			this.loading.orphanReport = true;
			this.loadingCategory = category;

			try {
				const { data } = await this.axios.get("/devops/orphans/check", {
					params: {
						detailed: true,
						category
					}
				});

				this.$set(this.orphanCategoryResults, category, data);
				this.rebuildMergedOrphanReport();
				this.$success(this.$t("orphan-report-success"));
			} catch (err) {
				this.$error(
					err.response?.data?.message || this.$t("error.orphan-report")
				);
			} finally {
				this.loading.orphanReport = false;
				this.loadingCategory = null;
			}
		},

		// Re-scan every category that has already been fetched.
		async refreshOrphanReport() {
			const categories = Object.keys(this.orphanCategoryResults);

			for (const category of categories) {
				await this.fetchOrphanReport(category);
			}
		},

		// Merge the per-category results into a single report object so the existing
		// summary/findings views keep working unchanged.
		rebuildMergedOrphanReport() {
			const results = Object.values(this.orphanCategoryResults);

			if (!results.length) {
				this.orphanReport = null;
				return;
			}

			const findings = results.flatMap(result =>
				Array.isArray(result.findings) ? result.findings : []
			);
			const scannedDocuments = results.reduce(
				(total, result) => total + (result.scannedDocuments || 0),
				0
			);
			const cleanupEligibleFindings = findings.filter(
				finding => finding.cleanupEligible
			).length;

			this.orphanReport = {
				generatedAt: new Date().toISOString(),
				scannedDocuments,
				summary: {
					totalOrphanFindings: findings.length,
					cleanupEligibleFindings
				},
				findings
			};
		},

		getPortingStatusEmoji(status) {
			if (status === true) return "✅";
			if (status === "MAYBE") return "⚠️";
			return "❌";
		},

		getHostedSmsStatusEmoji(status) {
			if (status === "ELIGIBLE") return "✅";
			if (status === "MAYBE") return "⚠️";
			return "❌";
		},

		getSubscriptionCount(subscriptions) {
			return subscriptions?.value?.length || 0;
		},

		getSubscriptions(subscriptions) {
			return subscriptions?.value || [];
		},

		formatResource(resource) {
			if (!resource) return "—";

			// Extract folder name from resource path
			const match = resource.match(/mailFolders\('([^']+)'\)/);

			if (match) {
				const folder = match[1];

				return this.folderNames[folder.toLowerCase()] || folder;
			}

			return resource;
		},

		formatScope(scope) {
			// For Google scopes, extract just the permission name from the URL
			if (scope.startsWith("https://www.googleapis.com/auth/")) {
				return scope.replace("https://www.googleapis.com/auth/", "");
			}
			return scope;
		},

		getChangeTypeColor(changeType) {
			const colors = {
				created: "success",
				updated: "info",
				deleted: "error"
			};

			return colors[changeType] || "grey";
		},

		isExpired(dateTime) {
			if (!dateTime) return true;
			return new Date(dateTime) < new Date();
		},

		formatDateTime(dateTime) {
			if (!dateTime) return "—";

			const date = new Date(dateTime);

			return date.toLocaleString(this.$i18n?.locale || "fr-FR", {
				year: "numeric",
				month: "short",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit"
			});
		},

		formatResponseTime(ms) {
			if (ms < 1000) {
				return `${ms}ms`;
			}
			return `${(ms / 1000).toFixed(2)}s`;
		},

		getWatchStatusCardClass(watchStatus) {
			if (!watchStatus.success) {
				return "watch-status-error";
			}
			if (watchStatus.watchActive) {
				return "watch-status-success";
			}
			return "watch-status-warning";
		},

		hasOAuthWarning(group) {
			if (group.emailType === "google" && group.watchStatus && !group.watchStatus.watchActive) {
				return true;
			}

			if (group.emailType === "MICROSOFT" && this.getSubscriptionCount(group.subscriptions) < 2) {
				return true;
			}

			return false;
		},

		async deleteOAuthCredential(group, cred) {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("oauth2-debug-delete-credential-title"),
					body: this.$t("oauth2-debug-delete-credential-confirm", { email: group.email, type: cred.type }),
					confirm: this.$t("delete"),
					cancel: this.$t("cancel")
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			this.$set(this.deleteCredentialLoading, cred.id, true);

			try {
				await this.axios.delete(`/devops/oauth2/credential/${cred.id}`);

				this.$success(this.$t("oauth2-debug-delete-credential-success"));
				await this.refreshOAuth2DebugData();
			} catch (err) {
				console.error("Error deleting OAuth credential:", err);
				this.$error(this.$t("error.oauth2-delete-credential"));
			} finally {
				this.$set(this.deleteCredentialLoading, cred.id, false);
			}
		},

		async forceResubscribe(groupId) {
			this.$set(this.resubscribeLoading, groupId, true);

			try {
				const response = await this.axios.post(`/devops/oauth2/force-resubscribe?id=${groupId}`);

				if (response.data?.success) {
					this.$success(this.$t("oauth2-debug-resubscribe-success"));
					// Reload the dialog data
					await this.refreshOAuth2DebugData();
				} else {
					throw new Error(response.data?.error || "Unknown error");
				}
			} catch (err) {
				console.error("Error forcing resubscribe:", err);
				const errorMessage = err.response?.data?.message || err.message || this.$t("error.oauth2-resubscribe");

				this.$error(errorMessage);
			} finally {
				this.$set(this.resubscribeLoading, groupId, false);
			}
		},

		async refreshOAuth2DebugData() {
			this.loading.oauth2Debug = true;

			try {
				const response = await this.axios.get("/devops/oauth2/debug");

				this.oauth2DebugResult = response.data;
			} catch (err) {
				console.error("Error refreshing OAuth2 debug info:", err);
				this.$error(this.$t("error.oauth2-debug"));
			} finally {
				this.loading.oauth2Debug = false;
			}
		},

		async openMigrateDialog() {
			this.migrateDialog = true;
			this.loadingGroups = true;
			this.loadingFromPhones = true;

			try {
				const [groups, phonesResponse] = await Promise.all([
					this.getAllGroups(true),
					this.axios.get(`/devops/phones?id=${this.organizationId}`)
				]);

				this.userGroups = groups;
				this.allPhones = phonesResponse.data?.result || [];
			} catch (err) {
				console.error("Error fetching data:", err);
				this.$error(this.$t("error.fetch-data"));
			} finally {
				this.loadingGroups = false;
				this.loadingFromPhones = false;
			}
		},

		closeMigrateDialog() {
			this.migrateDialog = false;
			this.migrateData = { from: "", to: "" };
			this.$refs.migrateForm?.reset();
		},

		async confirmMigrate() {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("migrate-conversations"),
					body: this.$t("migrate-conversations-confirm"),
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel")
				});

				await this.migrateConversations();
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}
		},

		async migrateConversations() {
			this.loading.migrate = true;

			try {
				await this.axios.post(`/conversations/migrate/${this.organizationId}`, {
					from: this.migrateData.from,
					to: this.migrateData.to
				});
				this.$success(this.$t("migrate-conversations-success"));
				this.closeMigrateDialog();
			} catch (err) {
				console.error("Error migrating conversations:", err);
				this.$error(this.$t("error.migrate-conversations"));
			} finally {
				this.loading.migrate = false;
			}
		},

		openPortabilityDialog() {
			this.portabilityDialog = true;
			this.portabilityPhone = "";
			this.portabilityResult = null;
		},

		closePortabilityDialog() {
			this.portabilityDialog = false;
			this.portabilityPhone = "";
			this.portabilityResult = null;
			this.$refs.portabilityForm?.reset();
		},

		async checkPortability() {
			this.loading.portabilityCheck = true;

			try {
				const response = await this.axios.get(`/devops/check-portability?phone=${encodeURIComponent(this.portabilityPhone)}`);

				this.portabilityResult = response.data;

				// Process Complete Porting status
				const portingPortability = this.portabilityResult.portingPortability;
				const notPortableReason = portingPortability?.notPortableReason;

				if (notPortableReason === "MANUAL_PORTING_AVAILABLE") {
					portingPortability.portable = true;
				} else if (
					portingPortability?.portable === false &&
					notPortableReason &&
					notPortableReason !== "UNSUPPORTED" &&
					notPortableReason !== "INVALID_PHONE_NUMBER"
				) {
					portingPortability.portable = "MAYBE";
				}

				// Process Hosted SMS Porting status
				const hostedSmsPortability = this.portabilityResult.hostedSmsPortability;
				const eligibilityStatus = hostedSmsPortability?.eligibility_status;
				const nextStep = hostedSmsPortability?.next_step;

				if (
					eligibilityStatus !== "ELIGIBLE" &&
					nextStep &&
					nextStep !== "NUMBER_TYPE_CANNOT_BE_HOSTED" &&
					nextStep !== "None" &&
					nextStep !== "NONE" &&
					nextStep !== "EDIT_INELIGIBLE_NUMBER_AND_RUN_ELIGIBILITY_CHECK_AGAIN"
				) {
					hostedSmsPortability.eligibility_status = "MAYBE";
				}
			} catch (err) {
				console.error("Error checking portability:", err);
				this.$error(this.$t("error.portability-check"));
			} finally {
				this.loading.portabilityCheck = false;
			}
		},

		openPhoneWebhooksDialog() {
			this.phoneWebhooksDialog = true;
			this.phoneWebhooksAllOrgs = false;
			this.phoneWebhooksResult = null;
		},

		closePhoneWebhooksDialog() {
			this.phoneWebhooksDialog = false;
			this.phoneWebhooksResult = null;
		},

		async fetchPhoneWebhooks(repair = false) {
			if (repair) {
				this.repairingWebhooks = true;
			} else {
				this.loading.phoneWebhooks = true;
			}

			try {
				const params = {};

				if (!this.phoneWebhooksAllOrgs) {
					params.id = this.organizationId;
				}

				const { data } = repair
					? await this.axios.post("/devops/phones/webhooks/repair", null, { params })
					: await this.axios.get("/devops/phones/webhooks", { params });

				this.phoneWebhooksResult = data;

				if (repair && data.repaired) {
					this.$success(this.$t("phone-webhooks-repaired", { count: data.repaired }));
				}
			} catch (err) {
				console.error("Error auditing phone webhooks:", err);
				this.$error(this.$t("error.phone-webhooks"));
			} finally {
				this.loading.phoneWebhooks = false;
				this.repairingWebhooks = false;
			}
		},

		async repairPhoneWebhooks() {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("phone-webhooks-repair"),
					body: this.$t("phone-webhooks-repair-confirm", {
						count: this.phoneWebhooksResult?.mismatches?.length || 0
					}),
					confirm: this.$t("phone-webhooks-repair"),
					cancel: this.$t("cancel")
				});
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}

			await this.fetchPhoneWebhooks(true);
		},

		async openOAuth2DebugDialog() {
			this.oauth2DebugDialog = true;
			this.oauth2DebugResult = null;
			this.resubscribeLoading = {};
			this.loading.oauth2Debug = true;

			try {
				const response = await this.axios.get("/devops/oauth2/debug");

				this.oauth2DebugResult = response.data;
			} catch (err) {
				console.error("Error fetching OAuth2 debug info:", err);
				this.$error(this.$t("error.oauth2-debug"));
				this.oauth2DebugDialog = false;
			} finally {
				this.loading.oauth2Debug = false;
			}
		},

		closeOAuth2DebugDialog() {
			this.oauth2DebugDialog = false;
			this.oauth2DebugResult = null;
			this.resubscribeLoading = {};
		},

		async confirmAction(action) {
			const actionConfig = {
				purge: {
					title: this.$t("purge-all-contacts"),
					body: this.$t("purge-all-contacts-confirm"),
					method: this.purgeAllContacts
				},
				reindexCurrent: {
					title: this.$t("reindex-contacts-current"),
					body: this.$t("reindex-contacts-current-confirm"),
					method: this.reindexCurrentOrganization
				},
				reindexAll: {
					title: this.$t("reindex-contacts-all"),
					body: this.$t("reindex-contacts-all-confirm"),
					method: this.reindexAllOrganizations
				}
			};

			const config = actionConfig[action];

			try {
				await this.$refs.dialogRef.open({
					title: config.title,
					body: config.body,
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel"),
					showCloseButton: true
				});

				await config.method();
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}
		},

		async purgeAllContacts() {
			this.loading.purge = true;
			try {
				await this.axios.post(`/devops/purge-contacts?id=${this.organizationId}`);
				this.$success(this.$t("purge-all-contacts-success"));
			} catch (err) {
				console.error("Error purging contacts:", err);
				this.$error(this.$t("error.purge-all-contacts"));
			} finally {
				this.loading.purge = false;
			}
		},

		async reindexCurrentOrganization() {
			this.loading.reindexCurrent = true;
			try {
				await this.axios.post("/patients-search/reformat-organization", { organizationId: this.organizationId });
				this.$success(this.$t("reindex-contacts-current-success"));
			} catch (err) {
				console.error("Error reindexing contacts:", err);
				this.$error(this.$t("error.reindex-contacts-current"));
			} finally {
				this.loading.reindexCurrent = false;
			}
		},

		async reindexAllOrganizations() {
			this.loading.reindexAll = true;
			try {
				await this.axios.post("/patients-search/reformat-index");
				this.$success(this.$t("reindex-contacts-all-success"));
			} catch (err) {
				console.error("Error reindexing all contacts:", err);
				this.$error(this.$t("error.reindex-contacts-all"));
			} finally {
				this.loading.reindexAll = false;
			}
		},

		openCreateAgentDialog() {
			this.createAgentData = {
				organizationId: "",
				erpType: "",
				dbServer: "",
				enabled: true,
				downloadFile: true
			};
			this.createAgentDialog = true;
		},

		closeCreateAgentDialog() {
			this.createAgentDialog = false;
			this.$refs.createAgentForm?.reset();
		},

		async createAgent() {
			if (!this.$refs.createAgentForm.validate()) return;

			this.loading.createAgent = true;
			try {
				const { erpType, organizationId, dbServer, enabled } = this.createAgentData;

				const { data } = await this.axios.post(
					`/patients/integrations/admin/agents/${erpType}/${organizationId}`,
					{ dbServer, enabled }
				);
				const downloadFile = this.createAgentData.downloadFile && data.id;

				this.$success(this.$t("create-agent-success"));
				this.closeCreateAgentDialog();

				// Show license key dialog if available
				if (data.licenseKey) {
					this.agentLicenseAgentId = data.id;
					this.agentLicenseKey = data.licenseKey;
					this.licenseKeyDialog = true;
				}

				// The agent exists already: a failed download must not hide its one-time key
				if (downloadFile) {
					await this.downloadInstallerFile().catch(err => {
						console.error("Error downloading installer:", err);
						this.$error(this.$t("error.download-installer"));
					});
				}
			} catch (err) {
				console.error("Error creating agent:", err);
				this.$error(err.response?.data?.message || this.$t("error.create-agent"));
			} finally {
				this.loading.createAgent = false;
			}
		},

		async downloadInstallerFile() {
			const response = await this.axios.get(
				"/patients/integrations/admin/agents/release/installer",
				{ responseType: "blob" }
			);

			const blob = new Blob([response.data], { type: "application/octet-stream" });
			const link = document.createElement("a");
			const filename = /filename="?([^";]+)"?/.exec(response.headers["content-disposition"] ?? "")?.[1];

			link.href = URL.createObjectURL(blob);
			link.download = filename || "FreskAgent.msi";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(link.href);
		},

		copyLicenseKey() {
			navigator.clipboard.writeText(this.agentLicenseKey);
			this.$success(this.$t("copied-to-clipboard"));
		},

		copyToClipboard(text) {
			navigator.clipboard.writeText(text);
			this.$success(this.$t("copied-to-clipboard"));
		},

		openCreateErpIntegrationDialog() {
			this.createErpIntegrationData = {
				organizationId: "",
				erpType: ""
			};
			this.createErpIntegrationDialog = true;
		},

		closeCreateErpIntegrationDialog() {
			this.createErpIntegrationDialog = false;
			this.$refs.createErpIntegrationForm?.reset();
		},

		async createErpIntegration() {
			if (!this.$refs.createErpIntegrationForm.validate()) return;

			this.loading.createErpIntegration = true;
			try {
				const { erpType, organizationId } = this.createErpIntegrationData;

				await this.axios.post(
					`/patients/integrations/admin/rest/${erpType}/${organizationId}`,
					{ enabled: true }
				);

				this.$success(this.$t("create-erp-integration-success"));
				this.closeCreateErpIntegrationDialog();
			} catch (err) {
				console.error("Error creating ERP integration:", err);
				this.$error(err.response?.data?.message || this.$t("error.create-erp-integration"));
			} finally {
				this.loading.createErpIntegration = false;
			}
		},

		async openCreateConnectorDialog() {
			this.createConnectorData = {
				organizationId: "",
				type: "",
				crmId: "",
				username: "",
				password: ""
			};
			this.createConnectorDialog = true;

			await Promise.all([
				this.loadConnectorTypes(),
				this.loadConnectorOrganizationsList()
			]);
		},

		closeCreateConnectorDialog() {
			this.createConnectorDialog = false;
			this.$refs.createConnectorForm?.reset();
		},

		async loadConnectorTypes() {
			this.loadingConnectorTypes = true;
			try {
				this.connectorTypes = await connectorDevopsService.listConnectors();
			} catch (err) {
				console.error("Error loading connector types:", err);
				this.connectorTypes = [];
			} finally {
				this.loadingConnectorTypes = false;
			}
		},

		async loadConnectorOrganizationsList() {
			this.loadingConnectorOrganizations = true;
			try {
				this.connectorOrganizations = (
					await this.axios.get("/devops/organizations")
				).data;
			} catch (err) {
				console.error("Error loading organizations:", err);
				this.connectorOrganizations = [];
			} finally {
				this.loadingConnectorOrganizations = false;
			}
		},

		trimConnectorField(field) {
			const v = this.createConnectorData[field];

			if (typeof v === "string") {
				this.createConnectorData[field] = v.trim();
			}
		},

		async createConnector() {
			if (!this.$refs.createConnectorForm.validate()) return;

			const { organizationId, type, crmId, username, password } =
				this.createConnectorData;

			const payload = { crmId: crmId.trim() };

			if (username?.trim()) payload.username = username.trim();
			if (password?.trim()) payload.password = password.trim();

			this.loading.createConnector = true;

			try {
				await connectorDevopsService.createOrganizationConnector(
					type,
					organizationId,
					payload
				);

				this.$success(this.$t("connector.create-success"));
				this.closeCreateConnectorDialog();
			} catch (err) {
				console.error("Error creating connector:", err);

				const status = err.response?.status;
				const fallback =
					status === 409
						? this.$t("connector.error.duplicate")
						: status === 400
							? this.$t("connector.error.bad-request")
							: status === 403
								? this.$t("connector.error.forbidden")
								: this.$t("connector.error.create-failed");

				this.$error(err.response?.data?.message || fallback);
			} finally {
				this.loading.createConnector = false;
			}
		},

		openTwilioRecordingsDialog() {
			this.twilioRecordingsDialog = true;
			this.twilioRecordingsResults = null;
			this.twilioRecordingsData = {
				startDate: "",
				startTime: "00:00",
				endDate: "",
				endTime: "23:59",
				timezone: "America/Montreal"
			};
		},

		closeTwilioRecordingsDialog() {
			this.twilioRecordingsDialog = false;
			this.twilioRecordingsResults = null;
			this.twilioRecordingsSearch = "";
			this.$refs.twilioRecordingsForm?.reset();
		},

		async fetchTwilioRecordings() {
			if (!this.$refs.twilioRecordingsForm.validate()) return;

			this.loading.twilioRecordings = true;
			this.twilioRecordingsResults = null;

			try {
				const { startDate, startTime, endDate, endTime, timezone } = this.twilioRecordingsData;

				const dateCreatedAfter = `${startDate}T${startTime}:00`;
				const dateCreatedBefore = `${endDate}T${endTime}:59`;

				const { data } = await this.axios.get("/devops/twilio/recordings", {
					params: {
						dateCreatedAfter,
						dateCreatedBefore,
						timezone
					}
				});

				this.twilioRecordingsResults = data;
			} catch (err) {
				console.error("Error fetching Twilio recordings:", err);
				this.$error(this.$t("error.fetch-twilio-recordings"));
			} finally {
				this.loading.twilioRecordings = false;
			}
		},

		formatDuration(seconds) {
			if (!seconds) return "0:00";
			const mins = Math.floor(seconds / 60);
			const secs = seconds % 60;

			return `${mins}:${secs.toString().padStart(2, "0")}`;
		},

		getRecordingStatusColor(status) {
			const colors = {
				completed: "success",
				processing: "info",
				"in-progress": "warning",
				failed: "error"
			};

			return colors[status] || "grey";
		},

		formatPhoneForFilename(value) {
			if (!value) return "";
			// Handle SIP URIs: strip "sip:" prefix and everything after "@"
			let cleaned = value.replace(/^sip:/i, "").split("@")[0];
			// Keep only digits and + for phone numbers

			return `_${cleaned.replace(/[^0-9+a-zA-Z]/g, "")}`;
		},

		async downloadRecording(recording) {
			try {
				const twilioUrl = encodeURIComponent(recording.mediaUrl + ".mp3");

				const response = await this.axios.get(`/voice/v1/recordings/download?url=${twilioUrl}`, {
					responseType: "blob"
				});

				const blob = response.data;
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");

				const date = recording.dateCreated.replace(/[/:]/g, "-");
				const from = this.formatPhoneForFilename(recording.from);
				const to = this.formatPhoneForFilename(recording.to);

				link.href = url;
				link.download = `recording-${date}${from}${to}.mp3`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			} catch (err) {
				console.error("Error downloading recording:", err);
				this.$error(this.$t("error.download-recording"));
			}
		},

		async downloadAllRecordings() {
			const recordings = [...this.filteredTwilioRecordings];
			const batchSize = 5;
			const delayMs = 1000;

			this.downloadingAllRecordings = true;

			try {
				for (let i = 0; i < recordings.length; i += batchSize) {
					const batch = recordings.slice(i, i + batchSize);

					await Promise.all(batch.map(recording => this.downloadRecording(recording)));

					if (i + batchSize < recordings.length) {
						await new Promise(resolve => setTimeout(resolve, delayMs));
					}
				}
			} finally {
				this.downloadingAllRecordings = false;
			}
		},

		async openCallerIdDialog() {
			this.callerIdDialog = true;
			this.callerIdPhone = "";
			this.callerIdExtension = "";
			this.callerIdFriendlyName = "";
			this.callerIdResult = null;
			this.callerIdStatus = null;

			// Prefill with organization phone
			try {
				const { data } = await this.axios.get("/devops/caller-id/phone");

				if (data) {
					this.callerIdPhone = data;
				}
			} catch {
				// Ignore - user can type manually
			}
		},

		closeCallerIdDialog() {
			this.callerIdDialog = false;
			this.callerIdResult = null;
			this.callerIdStatus = null;
			this.callerIdPhone = "";
			this.callerIdExtension = "";
			this.callerIdFriendlyName = "";
			this.stopCallerIdPolling();
			this.$refs.callerIdForm?.reset();
		},

		async submitCallerIdValidation() {
			this.loading.callerIdValidation = true;

			try {
				const payload = {
					phoneNumber: this.callerIdPhone,
					friendlyName: this.callerIdFriendlyName
				};

				if (this.callerIdExtension) {
					payload.extension = this.callerIdExtension;
				}

				const { data } = await this.axios.post("/devops/caller-id/validate", payload);

				this.callerIdResult = data;
				this.callerIdStatus = "pending";
				this.startCallerIdPolling(data.callSid);
			} catch (err) {
				console.error("Error creating caller ID validation:", err);
				this.$error(this.$t("error.caller-id-validation"));
			} finally {
				this.loading.callerIdValidation = false;
			}
		},

		async retryCallerIdValidation() {
			this.loading.callerIdValidation = true;
			this.stopCallerIdPolling();

			try {
				const payload = {
					phoneNumber: this.callerIdPhone,
					friendlyName: this.callerIdFriendlyName
				};

				if (this.callerIdExtension) {
					payload.extension = this.callerIdExtension;
				}

				const { data } = await this.axios.post("/devops/caller-id/validate", payload);

				this.callerIdResult = data;
				this.callerIdStatus = "pending";
				this.startCallerIdPolling(data.callSid);
			} catch (err) {
				console.error("Error retrying caller ID validation:", err);
				this.$error(this.$t("error.caller-id-validation"));
			} finally {
				this.loading.callerIdValidation = false;
			}
		},

		startCallerIdPolling(callSid) {
			this.stopCallerIdPolling();

			this.callerIdPollingTimer = setInterval(async () => {
				try {
					const { data } = await this.axios.get(`/devops/caller-id/status/${callSid}`);

					this.callerIdStatus = data.status;

					if (data.status !== "pending") {
						this.stopCallerIdPolling();
					}
				} catch {
					this.stopCallerIdPolling();
					this.callerIdStatus = "expired";
				}
			}, 5000);
		},

		stopCallerIdPolling() {
			if (this.callerIdPollingTimer) {
				clearInterval(this.callerIdPollingTimer);
				this.callerIdPollingTimer = null;
			}
		},

		formatE164ToDisplay(phone) {
			if (!phone) return "";

			// North American: +1XXXXXXXXXX -> (XXX) XXX-XXXX
			const naMatch = phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/);

			if (naMatch) {
				return `(${naMatch[1]}) ${naMatch[2]}-${naMatch[3]}`;
			}

			return phone;
		},

		getOrgDisplayName(name) {
			if (!name) return "";

			if (typeof name === "object") {
				const locale = this.$i18n?.locale || "fr-FR";

				return name[locale] || name["fr-FR"] || Object.values(name)[0] || "—";
			}

			return name;
		},

		async openTransferPhoneDialog() {
			this.transferPhoneDialog = true;
			this.transferPhoneData = {
				sourceAccountSid: "",
				destinationAccountSid: "",
				phoneNumberSid: ""
			};
			this.transferPhoneNumbers = [];
			this.loadingTransferPhoneAccounts = true;

			try {
				const { data } = await this.axios.get("/devops/organizations/accounts");

				this.transferPhoneAccounts = data;
			} catch (err) {
				console.error("Error fetching organizations with accounts:", err);
				this.$error(this.$t("error.transfer-phone-number"));
			} finally {
				this.loadingTransferPhoneAccounts = false;
			}
		},

		closeTransferPhoneDialog() {
			this.transferPhoneDialog = false;
			this.transferPhoneData = {
				sourceAccountSid: "",
				destinationAccountSid: "",
				phoneNumberSid: "",
				addressSid: ""
			};
			this.transferPhoneAccounts = null;
			this.transferPhoneNumbers = [];
			this.transferPhoneAddresses = [];
			this.$refs.transferPhoneForm?.reset();
		},

		async onSourceAccountChange() {
			this.transferPhoneData.phoneNumberSid = "";
			this.transferPhoneNumbers = [];

			if (!this.transferPhoneData.sourceAccountSid) return;

			this.loadingTransferPhoneNumbers = true;

			try {
				const { data } = await this.axios.get(
					`/devops/twilio/phone-numbers/${this.transferPhoneData.sourceAccountSid}`
				);

				this.transferPhoneNumbers = data;
			} catch (err) {
				console.error("Error fetching phone numbers:", err);
				this.$error(this.$t("error.transfer-phone-fetch-numbers"));
			} finally {
				this.loadingTransferPhoneNumbers = false;
			}
		},

		async onDestinationAccountChange() {
			this.transferPhoneData.addressSid = "";
			this.transferPhoneAddresses = [];

			if (!this.transferPhoneData.destinationAccountSid) return;

			this.loadingTransferPhoneAddresses = true;

			try {
				const { data } = await this.axios.get(
					`/devops/twilio/addresses/${this.transferPhoneData.destinationAccountSid}`
				);

				this.transferPhoneAddresses = data;
			} catch (err) {
				console.error("Error fetching addresses:", err);
				this.$error(this.$t("error.transfer-phone-fetch-addresses"));
			} finally {
				this.loadingTransferPhoneAddresses = false;
			}
		},

		async confirmTransferPhone() {
			try {
				await this.$refs.dialogRef.open({
					title: this.$t("transfer-phone-number"),
					body: this.$t("transfer-phone-number-confirm"),
					confirm: this.$t("confirm"),
					cancel: this.$t("cancel")
				});

				await this.executeTransferPhone();
			} catch (err) {
				if (err === "cancel") return;
				throw err;
			}
		},

		async executeTransferPhone() {
			this.loading.transferPhone = true;

			try {
				const payload = {
					sourceAccountSid: this.transferPhoneData.sourceAccountSid,
					destinationAccountSid: this.transferPhoneData.destinationAccountSid,
					phoneNumberSid: this.transferPhoneData.phoneNumberSid
				};

				if (this.transferPhoneData.addressSid) {
					payload.addressSid = this.transferPhoneData.addressSid;
				}

				const { data } = await this.axios.post("/devops/twilio/transfer-phone-number", payload);

				this.$success(
					this.$t("transfer-phone-number-success", { phoneNumber: data.phoneNumber })
				);
				this.closeTransferPhoneDialog();
			} catch (err) {
				console.error("Error transferring phone number:", err);
				this.$error(this.$t("error.transfer-phone-number"));
			} finally {
				this.loading.transferPhone = false;
			}
		}
	}
};
</script>

<style scoped>
	.action-item {
		min-height: 72px;
		border-radius: 12px;
	}

	.transfer-phone-alert::v-deep .v-alert__icon {
		align-self: center;
	}

	.text-wrap {
		white-space: normal !important;
	}

	.avancie-tenants-list {
		max-height: 150px;
		overflow-y: auto;
		border: 1px solid rgba(103, 58, 183, 0.15);
		border-radius: 12px;
		padding: 8px;
		background: rgba(103, 58, 183, 0.02);
	}

	.orphan-report-card {
		max-height: 90vh;
		display: flex;
		flex-direction: column;
		border-radius: 20px !important;
	}

	.orphan-report-card > .v-card__text {
		overflow-y: auto;
		flex: 1 1 auto;
	}

	.orphan-report-card > .v-card__actions {
		flex: 0 0 auto;
	}

	.phone-webhooks-card {
		max-height: 90vh;
		display: flex;
		flex-direction: column;
		border-radius: 20px !important;
	}

	.phone-webhooks-card > .v-card__text {
		overflow-y: auto;
		flex: 1 1 auto;
		min-height: 0;
	}

	.phone-webhooks-card > .v-card__title,
	.phone-webhooks-card > .v-divider,
	.phone-webhooks-card > .v-card__actions {
		flex: 0 0 auto;
	}

	.orphan-report-panel {
		max-height: 56vh;
		overflow-y: auto;
		border: 1px solid rgba(244, 67, 54, 0.15);
		border-radius: 12px;
		padding: 10px;
		background: rgba(244, 67, 54, 0.03);
	}

	.orphan-report-metrics {
		gap: 8px;
	}

	.orphan-report-metric {
		min-width: 140px;
		padding: 6px 8px;
		border-radius: 10px;
		background: rgba(255, 255, 255, 0.8);
		border: 1px solid rgba(0, 0, 0, 0.05);
	}

	.orphan-findings-preview {
		border-top: 1px dashed rgba(0, 0, 0, 0.12);
		padding-top: 8px;
	}

	.orphan-finding-line {
		font-family: monospace;
		line-height: 1.45;
	}

	.portability-section {
		background-color: rgba(0, 0, 0, 0.02);
		border-radius: 14px;
		padding: 14px;
		border: 1px solid rgba(0, 0, 0, 0.04);
	}

	.subscription-field {
		min-width: 120px;
	}

	.subscription-field-full {
		width: 100%;
	}

	.watch-status-field {
		min-width: 100px;
	}

	.watch-status-field-full {
		width: 100%;
	}

	.watch-status-success {
		border-color: #4caf50 !important;
		background-color: rgba(76, 175, 80, 0.05);
	}

	.watch-status-warning {
		border-color: #ff9800 !important;
		background-color: rgba(255, 152, 0, 0.05);
	}

	.watch-status-error {
		border-color: #f44336 !important;
		background-color: rgba(244, 67, 54, 0.05);
	}

	.oauth-error-code {
		display: block;
		word-wrap: break-word;
		word-break: break-all;
		white-space: pre-wrap;
		max-width: 100%;
	}

	/* Action Menu Styles */
	.action-menu-btn {
		width: 100%;
		cursor: pointer;
		transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
		border-color: rgba(25, 118, 210, 0.2) !important;
		border-radius: 16px !important;
	}

	.action-menu-btn:hover {
		border-color: #1976d2 !important;
		background-color: rgba(25, 118, 210, 0.03);
		transform: translateY(-1px);
		box-shadow: 0 4px 12px rgba(25, 118, 210, 0.1);
	}

	.action-menu-btn--active {
		border-color: #1976d2 !important;
		box-shadow: 0 4px 16px rgba(25, 118, 210, 0.2);
	}

	.actions-menu-card {
		max-height: 500px;
		overflow-y: auto;
		border-radius: 16px !important;
		box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.03) !important;
	}

	.action-menu-item {
		padding: 14px 16px;
		border-radius: 14px;
		transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		margin: 2px 0;
	}

	.action-menu-item:hover {
		background-color: rgba(0, 0, 0, 0.03);
	}

	.action-menu-item:not(:last-child) {
		border-bottom: 1px solid rgba(0, 0, 0, 0.05);
	}

	.action-menu-item.action-disabled {
		opacity: 0.45;
	}

	.action-description {
		line-height: 1.3;
	}

	.action-menu-item .v-avatar {
		border-radius: 12px !important;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	}

	.action-menu-item .v-btn {
		border-radius: 20px !important;
		text-transform: none !important;
		font-weight: 600 !important;
		letter-spacing: 0 !important;
		min-width: 80px;
		transition: all 0.2s ease !important;
	}

	.action-menu-item .v-btn:hover {
		transform: scale(1.04);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
	}

	.min-width-0 {
		min-width: 0;
	}

	/* Embedded Mode Styles */
	.embedded-actions-list {
		max-height: 400px;
		overflow-y: auto;
		padding: 8px 12px;
	}

	@media (max-width: 599px) {
		.embedded-actions-list {
			max-height: none;
		}
	}

	/* Mobile-only styles for xs viewport */
	.flex-wrap {
		flex-wrap: wrap !important;
	}

	.action-mobile {
		width: 100%;
		margin-top: 12px;
		margin-left: 0 !important;
		display: flex;
		justify-content: center;
		flex-basis: 100%;
	}

	.action-mobile .v-btn {
		width: 100%;
		max-width: 200px;
	}

	/* Twilio Recordings Dialog */
	.twilio-recordings-card {
		max-height: 90vh;
		display: flex;
		flex-direction: column;
		border-radius: 20px !important;
	}

	.twilio-recordings-card > .v-card__text {
		overflow-y: auto;
		flex: 1 1 auto;
	}

	.twilio-recordings-card > .v-card__actions {
		flex: 0 0 auto;
	}

	/* Recordings List Styles */
	.recordings-list {
		border: 1px solid rgba(0, 0, 0, 0.08);
		border-radius: 14px;
		overflow: hidden;
	}

	.recording-item {
		border-bottom: 1px solid rgba(0, 0, 0, 0.05);
		box-sizing: border-box;
		transition: background-color 0.15s ease;
	}

	.recording-item:last-child {
		border-bottom: none;
	}

	.recording-item:hover {
		background-color: rgba(0, 0, 0, 0.02);
	}

	.copy-icon {
		cursor: pointer;
		opacity: 0.6;
		transition: opacity 0.2s ease;
	}

	.copy-icon:hover {
		opacity: 1;
	}

	/* Caller ID Code Box */
	.caller-id-code-box {
		display: flex;
		justify-content: center;
		gap: 8px;
	}

	.caller-id-digit {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 56px;
		font-size: 28px;
		font-weight: 700;
		border-radius: 12px;
		background-color: rgba(25, 118, 210, 0.06);
		border: 2px solid rgba(25, 118, 210, 0.2);
		color: #1976d2;
		font-family: monospace;
		box-shadow: 0 2px 8px rgba(25, 118, 210, 0.08);
	}

	@media (max-width: 400px) {
		.caller-id-digit {
			width: 38px;
			height: 46px;
			font-size: 22px;
			gap: 4px;
		}
	}

	.oauth-id-info {
		text-align: right;
	}

	@media (max-width: 599px) {
		.oauth-user-card {
			flex-wrap: wrap;
		}

		.oauth-id-info {
			width: 100%;
			text-align: left;
			margin-top: 8px;
			padding-top: 8px;
			border-top: 1px solid #eee;
		}
	}
</style>

<style>
	/* ── Sub-Dialog Prettification (unscoped — v-dialog teleports to body) ── */

	.devops-subdialog {
		border-radius: 20px !important;
		overflow: hidden;
	}

	.devops-subdialog > .v-card {
		border-radius: 20px !important;
		box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.03) !important;
	}

	@media (max-width: 599px) {
		.devops-subdialog {
			border-radius: 0 !important;
		}

		.devops-subdialog > .v-card {
			border-radius: 0 !important;
		}
	}

	.devops-subdialog .v-card .v-card {
		box-shadow: none !important;
		border-radius: 0 !important;
	}

	.devops-subdialog .v-card__title {
		letter-spacing: -0.01em;
	}

	.devops-subdialog .v-divider {
		border-color: rgba(0, 0, 0, 0.06) !important;
	}

	.devops-subdialog .v-card__actions .v-btn {
		border-radius: 20px !important;
		text-transform: none !important;
		font-weight: 600 !important;
		letter-spacing: 0 !important;
		min-width: 90px;
		transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
	}

	.devops-subdialog .v-card__actions .v-btn:hover {
		transform: translateY(-1px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
	}

	.devops-subdialog .v-card--outlined {
		border-radius: 14px !important;
		border-color: rgba(0, 0, 0, 0.08) !important;
	}

	.devops-subdialog .v-expansion-panels {
		border-radius: 14px !important;
		overflow: hidden;
	}

	.devops-subdialog .v-expansion-panel {
		border-radius: 0 !important;
	}

	.devops-subdialog .v-expansion-panel::before {
		box-shadow: none !important;
	}

	.devops-subdialog .v-text-field--outlined fieldset {
		border-radius: 12px !important;
	}

	.devops-subdialog .v-chip {
		border-radius: 10px !important;
		font-weight: 600;
	}

	.devops-subdialog .v-alert {
		border-radius: 14px !important;
	}

	/* OAuth2 Debug Dialog — make the .v-dialog itself a flex container
	   so the card is forced to fit within the dialog's max-height: 90% */
	.devops-subdialog--scrollable {
		display: flex;
		flex-direction: column;
	}

	.oauth2-debug-card {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.oauth2-debug-card > .v-card__title,
	.oauth2-debug-card > .v-divider {
		flex: 0 0 auto;
	}

	.oauth2-debug-card > .v-card__text {
		overflow-y: auto;
		flex: 1 1 auto;
		min-height: 0;
	}

	.oauth2-debug-card > .v-card__actions {
		flex: 0 0 auto;
	}

	@media (max-width: 599px) {
		.v-dialog.devops-subdialog {
			max-width: 100% !important;
			margin: 0 !important;
		}

		.oauth2-debug-card {
			padding-left: 0 !important;
			padding-right: 0 !important;
		}

		.oauth2-debug-card > .v-card__text {
			padding-left: 4px !important;
			padding-right: 4px !important;
		}

		.oauth2-debug-card .v-expansion-panel-header {
			padding-left: 8px !important;
			padding-right: 8px !important;
		}

		.oauth2-debug-card .v-expansion-panel-content__wrap {
			padding-left: 8px !important;
			padding-right: 8px !important;
		}
	}

</style>